import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getT, formatT } from '../i18n';
import './ApplyForms.css';
import './AdminDashboard.css';
import './MessagesChat.css';
import MessagesChat from './MessagesChat';
import { getAppRequirementIssues, formatRequirementLabels } from '../utils/requirementRedirect';
import { getHiredRequirementStatus, getPendingHiredRequirements } from '../utils/hiredRequirements';
import {
  getAppField,
  formatApplicationNo,
  formatApplicantLegalName,
  formatRehireEligibility,
  formatInterviewDate,
  resolveApplicantEmailFromApp,
  splitInterviewDateParts,
  combineInterviewDateParts,
  parseFormData,
} from '../utils/applicationFields';
import {
  filterDeletedMessages,
  groupMessagesIntoConversations,
  isChatMessage,
} from '../utils/messageConversations';
import { filterSubmittedJobApplications } from '../utils/applicationFilters';
import {
  canDepartmentReviewApplicant,
  canDepartmentSeeApplication,
  getApproveTargetStatus,
  getCurrentStageReviewer,
  getStageReviewer,
  getDepartmentKey,
  getRequiredStatusForDepartment,
  isSameReviewer,
  isFailedInterviewStatus,
  isPassedInterviewStatus,
  getFailedInterviewReviewer,
  getFailedInterviewReturnStatus,
  canReturnFailedInterviewToReview,
  isReviewQueueApplicant,
  isSharedQueueDepartment,
  usesInterviewerAssignment,
  getDepartmentKeyForStatus,
  PIPELINE_STATUS
} from '../utils/reviewPipeline';
import {
  isHeadAdminUser,
  isDepartmentAdminUser,
  isAllowedAdminTab,
  canViewAssessmentScores,
  canAccessAdminMessages,
} from '../utils/adminAccess';
import { getScoreGrade } from '../utils/assessmentScoring';
import EmploymentHistoryPanel from './EmploymentHistoryPanel';
import AttachmentInlineViewer from './AttachmentInlineViewer';
import MessagesFloatButton from './MessagesFloatButton';
import ChatToast from './ChatToast';
import SidebarNavIcon from './SidebarNavIcons';
import ProfileAvatarCropper from './ProfileAvatarCropper';
import hLogoImg from '../HLOGO.png';
import { JOB_POSITION_OPTIONS } from '../config/jobPositions';
import { API_BASE_URL, API_URL } from '../config/api';
import { buildAttachmentEmbedUrl, buildAttachmentUrl, openAttachmentViewer, verifyAttachmentUrl } from '../utils/attachmentViewer';
import { openDigitalResumeTab, openFullDetailsTab } from '../utils/applicantDocumentViewer';
import { notifyJobDescriptionsUpdated } from '../utils/jobDescriptions';
import {
  getAssignmentEntries,
  isApplicationAssignedToInterviewer,
  isApplicationAssignedToUser,
  isDepartmentInterviewerUser,
} from '../utils/assignmentHelpers';
import {
  collectChatFingerprint,
  countUnreadConversations,
  findNewChatAlerts,
} from '../utils/chatNotifications';

const PHONE_VIEWPORT_QUERY = '(max-width: 768px)';

const getAdminAvatarUrl = (admin) => {
  const filename = admin?.avatar;
  if (!filename) return null;
  return buildAttachmentEmbedUrl(filename);
};

const PersonAvatar = ({ admin, className, initial }) => {
  const avatarUrl = getAdminAvatarUrl(admin);
  return (
    <div className={`${className}${avatarUrl ? ' has-photo' : ''}`} aria-hidden="true">
      {avatarUrl ? (
        <img key={avatarUrl} src={avatarUrl} alt="" className="person-avatar-img" />
      ) : (
        initial
      )}
    </div>
  );
};

const usePhoneViewport = () => {
  const [isPhoneView, setIsPhoneView] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia(PHONE_VIEWPORT_QUERY).matches
  ));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mediaQuery = window.matchMedia(PHONE_VIEWPORT_QUERY);
    const syncViewport = () => setIsPhoneView(mediaQuery.matches);
    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);
    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  return isPhoneView;
};

const OnholdDetailField = ({ label, value, span = 1, href = '', onView = null }) => (
  <div className={`onhold-detail-field${span > 1 ? ' onhold-detail-field--wide' : ''}`}>
    <strong>{label}</strong>
    {onView ? (
      <button type="button" className="onhold-view-link" onClick={onView}>{value}</button>
    ) : href ? (
      <a href={href} target="_blank" rel="noopener noreferrer">{value}</a>
    ) : (
      <span className="onhold-field-value">{value || 'N/A'}</span>
    )}
  </div>
);

const PipelineEmptyState = ({ variant, message }) => (
  <div className={`pipeline-empty-state pipeline-empty-state--${variant}`}>
    <div className="pipeline-empty-icon" aria-hidden="true" />
    <p className="pipeline-empty-message">{message}</p>
  </div>
);

const PipelineApplicantHeader = ({ name, position, statusLabel, stageVariant }) => {
  const initial = (name || '?').charAt(0).toUpperCase();
  return (
    <div className="onhold-card-row pipeline-card-header">
      <div className={`pipeline-card-avatar pipeline-card-avatar--${stageVariant}`}>{initial}</div>
      <div className="onhold-card-heading">
        <p className="applicant-name">{name}</p>
        <p className="applicant-position">{position}</p>
      </div>
      <span className={`status-pill status-pill--${stageVariant}`}>{statusLabel}</span>
    </div>
  );
};

const INTERVIEWER_DEPT_CONFIG = [
  { key: 'HR', slug: 'hr', labelKey: 'deptHr', departmentKey: 'HR', status: PIPELINE_STATUS.PENDING_HR, sharedQueue: false },
  { key: 'Team Leader', slug: 'tl', labelKey: 'deptTl', departmentKey: 'TL', status: PIPELINE_STATUS.PENDING_TL, sharedQueue: true },
  { key: 'Operations', slug: 'ops', labelKey: 'deptOps', departmentKey: 'OPERATIONS', status: PIPELINE_STATUS.PENDING_OPS, sharedQueue: true },
  { key: 'Client', slug: 'client', labelKey: 'deptClient', departmentKey: 'CLIENT', status: PIPELINE_STATUS.PENDING_CLIENT, sharedQueue: false }
];

const POOLING_ACCOUNT_OPTIONS = ['Atome', 'Akulaku', 'Dreame', 'Fedex'];

const INVALID_ATTACHMENT_VALUES = new Set(['n/a', 'na', 'null', 'undefined', 'none', '-']);

const parseSavedRemarkEntries = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return [];

  const chunks = text
    .split(/(?=\[\d{4}-\d{2}-\d{2}\])/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  return chunks.map((chunk) => {
    const match = chunk.match(/^\[(\d{4}-\d{2}-\d{2})\]\s*(?:([^:\n]+):\s*)?([\s\S]*)$/);
    if (!match) {
      return { date: null, meta: '', body: chunk };
    }
    return {
      date: match[1],
      meta: String(match[2] || '').trim(),
      body: String(match[3] || '').trim(),
    };
  });
};

const formatSavedRemarkDate = (isoDate, lang = 'en') => {
  if (!isoDate) return '';
  const parsed = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const splitAttachmentValue = (value) => {
  if (value == null) return [];
  const trimmed = String(value).trim();
  if (!trimmed || INVALID_ATTACHMENT_VALUES.has(trimmed.toLowerCase())) return [];
  if (trimmed.includes('|||')) {
    return trimmed.split('|||').map((item) => item.trim()).filter(Boolean);
  }
  if (trimmed.startsWith('inline:')) {
    return [trimmed];
  }
  if (trimmed.startsWith('data:')) {
    return [`inline:${trimmed}`];
  }
  return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
};

const getAttachmentHint = (keys) => keys
  .map((key) => key.replace(/_files?$/, '').replace(/_/g, ''))
  .filter(Boolean);

const isServerStoredFilename = (value) => (
  /_files_[a-f0-9]{8,}\.[a-z0-9]+$/i.test(String(value).split(/[/\\]/).pop())
);

const collectAttachmentValues = (source, keys) => {
  const results = [];
  for (const key of keys) {
    results.push(...splitAttachmentValue(source?.[key]));
  }

  const hints = getAttachmentHint(keys);
  for (const [key, value] of Object.entries(source || {})) {
    const lowerKey = String(key).toLowerCase();
    const matchesHint = hints.some((hint) => lowerKey.includes(hint));
    if (!matchesHint) continue;
    if (!/(file|upload|attach|document|cert|clearance|resume|portfolio)/i.test(lowerKey)) continue;
    results.push(...splitAttachmentValue(value));
  }

  return results;
};

const getAttachmentFiles = (app, keys) => {
  const appValues = collectAttachmentValues(app, keys);
  const serverAppFiles = appValues.filter(isServerStoredFilename);
  if (serverAppFiles.length) return serverAppFiles;

  const formValues = collectAttachmentValues(parseFormData(app), keys);
  const serverFormFiles = formValues.filter(isServerStoredFilename);
  if (serverFormFiles.length) return serverFormFiles;

  if (appValues.length) return appValues;
  return formValues;
};

const getAttachmentCacheKey = (keys) => {
  const singular = keys.find((key) => key.endsWith('_file') && !key.endsWith('_files'));
  if (singular) return singular;
  const plural = keys.find((key) => key.endsWith('_files'));
  if (plural) return plural.replace('_files', '_file');
  return keys[0];
};

const getCachedAttachmentEntries = (app, keys) => {
  try {
    const email = (app?.email || '').trim().toLowerCase();
    if (!email) return [];
    const raw = localStorage.getItem(`helport_upload_cache_${email}`);
    if (!raw) return [];
    const cache = JSON.parse(raw);
    const cacheKey = getAttachmentCacheKey(keys);
    const entries = cache?.[cacheKey] || cache?.[`${cacheKey}s`] || [];
    return Array.isArray(entries) ? entries.filter((entry) => entry?.dataUrl) : [];
  } catch {
    return [];
  }
};

const resolveAttachmentPreviewItems = (app, doc) => {
  const files = getAttachmentFiles(app, doc.keys);
  if (files.length > 0) {
    return files.map((file, index) => {
      const label = files.length > 1 ? `${doc.label} (${index + 1})` : doc.label;
      const url = file.startsWith('inline:')
        ? file.slice('inline:'.length)
        : buildAttachmentEmbedUrl(file, label);
      return { label, url };
    });
  }

  const cachedEntries = getCachedAttachmentEntries(app, doc.keys);
  if (cachedEntries.length > 0) {
    return cachedEntries.map((entry, index) => ({
      label: cachedEntries.length > 1 ? `${doc.label} (${index + 1})` : doc.label,
      url: entry.dataUrl,
    }));
  }

  return [];
};

const buildSidebarMenu = (s) => ({
  main: [
    { id: 'app-review', label: s.applicantMonitor },
    { id: 'messages', label: s.messages || 'Messages' }
  ],
  settings: [
    { id: 'profile', label: s.account },
    { id: 'job-description', label: s.jobDescription, headAdminOnly: true }
  ],
  headAdmin: [
    { id: 'create-accounts', label: s.createAccounts },
    { id: 'interviewers', label: s.interviewers },
    { id: 'onhold', label: s.onHold },
    { id: 'job-offer', label: s.jobOffer },
    { id: 'hired', label: s.hired },
    { id: 'pooling', label: s.pooling },
    { id: 'passed-interview', label: s.passedInterview },
    { id: 'failed-interview', label: s.failedInterview },
    { id: 'application-retracted', label: s.retracted },
    { id: 'app-requirements-guard', label: s.requirementsGuard }
  ]
});

const buildStatCards = (a) => [
  { key: 'total', label: a.statTotal, variant: 'total', icon: '◉', tab: 'app-review' },
  { key: 'Pending HR', label: a.statPendingHr, variant: 'pending-hr', icon: '⏳', tab: 'app-review' },
  { key: 'Pending TL', label: a.statPendingTl, variant: 'pending-tl', icon: '👥', tab: 'app-review' },
  { key: 'Pending Ops', label: a.statPendingOps, variant: 'pending-ops', icon: '⚙', tab: 'app-review' },
  { key: 'Pending Client', label: a.statPendingClient, variant: 'pending-client', icon: '🤝', tab: 'app-review' },
  { key: 'Pending Head Admin', label: a.statPendingHeadAdmin, variant: 'pending-head-admin', icon: '★', tab: 'app-review' },
  { key: 'Hired', label: a.statHired, variant: 'hired', tab: 'hired' },
  { key: 'ONHOLD', label: a.statOnhold, variant: 'onhold', tab: 'onhold' },
  { key: 'JOB OFFER', label: a.statJobOffer, variant: 'job-offer', tab: 'job-offer' },
  { key: 'POOLING', label: a.statPooling, variant: 'pooling', tab: 'pooling' },
  { key: 'PASSED INTERVIEW', label: a.statPassedInterview, variant: 'passed-interview', icon: '✓', tab: 'passed-interview' },
  { key: 'FAILED INTERVIEW', label: a.statFailedInterview, variant: 'failed-interview', tab: 'failed-interview' },
  { key: 'APPLICATION RETRACTED', label: a.statRetracted, variant: 'retracted', tab: 'application-retracted' }
];

const getAppStatusClass = (status) => {
  const normalized = (status || '').trim().toLowerCase();
  if (normalized === 'pending hr') return 'app-status-pill--pending-hr';
  if (normalized === 'pending ops') return 'app-status-pill--pending-ops';
  if (normalized === 'pending tl') return 'app-status-pill--pending-tl';
  if (normalized === 'pending client') return 'app-status-pill--pending-client';
  if (normalized === 'pending head admin') return 'app-status-pill--pending-head-admin';
  if (normalized === 'hired') return 'app-status-pill--hired';
  if (isFailedInterviewStatus(normalized)) return 'app-status-pill--failed-interview';
  if (isPassedInterviewStatus(normalized)) return 'app-status-pill--passed-interview';
  if (/on\s*hold|onhold/.test(normalized)) return 'app-status-pill--onhold';
  if (/job\s*offer/.test(normalized)) return 'app-status-pill--job-offer';
  if (normalized === 'pooling') return 'app-status-pill--pooling';
  if (/application\s*retracted/.test(normalized)) return 'app-status-pill--retracted';
  return 'app-status-pill--default';
};

const isHiredApplicant = (app) =>
  (app?.status || '').trim().toLowerCase() === PIPELINE_STATUS.HIRED.toLowerCase();

const AdminDashboard = ({ currentUser, onBack, lang = 'zh', onLangChange, onProfileUpdate, applications, assignments, setAssignments, onSaveAssignment, fetchApplications, onFetchAssignments, onSaveInterviewDate, handleUpdateStatus, onClaimReview, onAssignAccount, onRetractApplication, handleSaveRemarks, notesInputs, handleNoteChange, messages = [], onReplyMessage, onDeleteMessage, onEditChatMessage, onDeleteChatMessage, hiddenMessageIds }) => {

  const t = getT(lang);
  const ta = t.admin;
  const tc = t.common;
  const ts = t.sidebar;
  const sidebarMenu = useMemo(() => buildSidebarMenu(ts), [ts]);
  const statCards = useMemo(() => buildStatCards(ta), [ta]);
  const attachmentDocs = useMemo(() => [
    { label: ta.attachments.resume, keys: ['resume_file', 'resume_files', 'resume'] },
    { label: ta.attachments.birth, keys: ['birth_cert_file', 'birth_cert_files', 'birth_certificate_file'] },
    { label: ta.attachments.coe, keys: ['coe_file', 'coe_files', 'clearance_file', 'coe_or_clearance'] },
    { label: ta.attachments.nbi, keys: ['nbi_file', 'nbi_files'] },
  ], [ta.attachments]);

  const [adminUsers, setAdminUsers] = useState([]);
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [newAdminRole, setNewAdminRole] = useState('HEAD ADMIN');
  const [newAdminPosition, setNewAdminPosition] = useState('');

  const [openAppCardId, setOpenAppCardId] = useState(null);
  const [openFailedCardId, setOpenFailedCardId] = useState(null);
  const [openPassedCardId, setOpenPassedCardId] = useState(null);
  const [openJobOfferCardId, setOpenJobOfferCardId] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [openHiredCardId, setOpenHiredCardId] = useState(null);
  const [openPoolingCardId, setOpenPoolingCardId] = useState(null);
  const [openGuardCardId, setOpenGuardCardId] = useState(null);
  const [openAssignInterviewerId, setOpenAssignInterviewerId] = useState(null);
  const [assignDraftByInterviewer, setAssignDraftByInterviewer] = useState({});
  const [assignSavingId, setAssignSavingId] = useState(null);
  const [interviewerSearch, setInterviewerSearch] = useState('');
  const [registeredAccountsSearch, setRegisteredAccountsSearch] = useState('');

  const [interviewDateInputs, setInterviewDateInputs] = useState({});
  const [interviewDateNotice, setInterviewDateNotice] = useState({});
  const [interviewDateSaving, setInterviewDateSaving] = useState({});
  const [assessmentMap, setAssessmentMap] = useState({});

  // --- MGA BAGONG STATE PARA SA SIDEBAR AT PROFILE UI ---
  const [activeTab, setActiveTab] = useState('app-review');
  const [userPosition, setUserPosition] = useState('');
  const [profileName, setProfileName] = useState('');
  const [profileUsername, setProfileUsername] = useState('');

  // --- STATE PARA SA EDIT AT CHANGE PASSWORD ---
  const [editingAdminId, setEditingAdminId] = useState(null);
  const [editAdminName, setEditAdminName] = useState('');
  const [editAdminEmail, setEditAdminEmail] = useState('');
  const [editAdminPosition, setEditAdminPosition] = useState('');
  const [changePasswordAdminId, setChangePasswordAdminId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordNotification, setPasswordNotification] = useState('');
  const [editingProfileField, setEditingProfileField] = useState(null);
  const [profileEmailDraft, setProfileEmailDraft] = useState('');
  const [profileUsernameDraft, setProfileUsernameDraft] = useState('');
  const [showProfilePassword, setShowProfilePassword] = useState(false);
  const [profileFieldNotice, setProfileFieldNotice] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [profileAvatarFile, setProfileAvatarFile] = useState(currentUser?.avatar || null);
  const [profileAvatarPreview, setProfileAvatarPreview] = useState(null);
  const [profileAvatarVersion, setProfileAvatarVersion] = useState(0);
  const [avatarCropSrc, setAvatarCropSrc] = useState(null);
  const avatarInputRef = useRef(null);
  const avatarPreviewUrlRef = useRef(null);
  const avatarCropUrlRef = useRef(null);
  const [showCreateAdminPassword, setShowCreateAdminPassword] = useState(false);
  const [showChangePasswordNew, setShowChangePasswordNew] = useState(false);
  const [showChangePasswordConfirm, setShowChangePasswordConfirm] = useState(false);
  // --- STATE PARA SA TIME CLOCK AT DATE ---
  const role = currentUser?.role || 'Applicant';

  const [replyInputs, setReplyInputs] = useState('');
  const [selectedMessageId, setSelectedMessageId] = useState(messages[0]?.id || null);
  const [chatToast, setChatToast] = useState(null);
  const chatFingerprintRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [retractedSearchQuery, setRetractedSearchQuery] = useState('');
  const [selectedJobPosition, setSelectedJobPosition] = useState('');
  const [jobDescriptions, setJobDescriptions] = useState({});
  const [jobDescriptionDraft, setJobDescriptionDraft] = useState('');
  const [jobDescriptionNotice, setJobDescriptionNotice] = useState('');
  const [isSavingJobDescription, setIsSavingJobDescription] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMonitorExpanded, setIsMonitorExpanded] = useState(false);
  const isPhoneView = usePhoneViewport();
  const appsSectionRef = useRef(null);
  const mainContentRef = useRef(null);

  const handleStatCardClick = (tabId = 'app-review') => {
    if (!isAllowedAdminTab(tabId, currentUser)) return;

    if (tabId === 'app-review') {
      if (activeTab !== 'app-review') {
        setActiveTab('app-review');
      }
      requestAnimationFrame(() => {
        appsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }

    setActiveTab(tabId);
    requestAnimationFrame(() => {
      mainContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const navigateToTab = (tabId) => {
    handleStatCardClick(tabId);
    closeMobileMenu();
  };

  const handleReplyChange = (value) => {
    setReplyInputs(value);
  };

  const handleSelectAttachmentIndex = async (index) => {
    let targetUrl = '';
    setAttachmentPreview((prev) => {
      if (!prev) return prev;
      targetUrl = prev.items[index]?.url || '';
      return { ...prev, index, loading: true, loadError: false };
    });
    if (!targetUrl) return;

    const canLoad = await verifyAttachmentUrl(targetUrl);
    setAttachmentPreview((prev) => {
      if (!prev || prev.index !== index) return prev;
      return {
        ...prev,
        loading: false,
        loadError: !canLoad,
        missingFilename: canLoad ? '' : (targetUrl.split('file=')[1]?.split('&')[0] || ''),
      };
    });
  };

  const handleSubmitReply = async () => {
    const text = replyInputs.trim();
    if (!text || !selectedMessageId) return;
    if (typeof onReplyMessage === 'function') {
      const sent = await onReplyMessage(selectedMessageId, text);
      if (sent !== false) setReplyInputs('');
    }
  };

  const getRemarksDraft = (app) => notesInputs[app.id] ?? '';

  const renderApplicantStageDetails = (app, { remarksLabel, remarksPlaceholder, showReasonForRetraction = false } = {}) => {
    const oh = ta.onHoldFields;
    const resumeFile = getAppField(app, ['resume_file', 'resume_files', 'resume'], '');
    const resumeUrl = resumeFile && resumeFile !== 'N/A'
      ? buildAttachmentUrl(resumeFile, ta.attachments?.resume || ta.viewResume)
      : '';

    return (
      <div className="onhold-details-grid">
        <OnholdDetailField label={oh.applicantName} value={formatApplicantLegalName(app)} />
        <OnholdDetailField label={oh.positionRole} value={getAppField(app, ['position', 'role'])} />
        <OnholdDetailField label={oh.mobileNo} value={getAppField(app, ['mobile_number', 'mobile', 'contact_number'])} />
        <OnholdDetailField
          label={oh.emailAddress}
          value={getAppField(app, ['email'])}
          href={getAppField(app, ['email'], '') !== 'N/A' ? `mailto:${getAppField(app, ['email'], '')}` : ''}
        />
        <OnholdDetailField
          label={oh.residentialAddress}
          value={getAppField(app, ['current_address', 'residential_address', 'permanent_address'])}
          span={2}
        />
        <OnholdDetailField label={oh.dateOfApplication} value={getAppField(app, ['date_of_application', 'dateApplied', 'created_at'])} />
        <OnholdDetailField label={oh.lastInterview} value={getAppField(app, ['last_interview_date', 'last_interview_assessment', 'last_interview'])} />
        <OnholdDetailField label={oh.rehireEligibility} value={formatRehireEligibility(app)} />
        <OnholdDetailField label={oh.reemploymentCount} value={getAppField(app, ['reemployment_count', 're_employment_count', 're_employment'])} />
        <OnholdDetailField label={oh.sourceOfReferral} value={getAppField(app, ['source_referral', 'source', 'referral_source'])} />
        <OnholdDetailField label={oh.referredBy} value={getAppField(app, ['referrer_name', 'referred_by', 'referred_by_name'])} />
        <OnholdDetailField
          label={oh.cvResume}
          value={resumeUrl ? ta.viewResume : 'N/A'}
          onView={resumeUrl ? () => openAttachmentViewer(resumeUrl, ta.attachments?.resume || ta.viewResume) : null}
        />
        <OnholdDetailField label={oh.dateOfBirth} value={getAppField(app, ['dateOfBirth', 'dob', 'date_of_birth', 'birth_date'])} />
        <OnholdDetailField label={oh.gender} value={getAppField(app, ['gender'])} />
        <OnholdDetailField label={oh.tin} value={getAppField(app, ['tin_number', 'tinNumber', 'tin'])} />
        <OnholdDetailField label={oh.sss} value={getAppField(app, ['sss_number', 'sssNumber', 'sss'])} />
        <OnholdDetailField label={oh.phic} value={getAppField(app, ['philhealth_number', 'philHealthNumber', 'phic'])} />
        <OnholdDetailField label={oh.hdmf} value={getAppField(app, ['pagibig_number', 'pagIbigNumber', 'hdmf'])} />
        <OnholdDetailField label={oh.nbiStatus} value={getAppField(app, ['nbi_status', 'nbiStatus'])} />
        {showReasonForRetraction && (
          <OnholdDetailField
            label={ta.reasonForRetraction}
            value={getAppField(app, ['retraction_reason', 'reason_for_retraction', 'retract_reason', 'withdrawal_reason'])}
            span={2}
          />
        )}
        <div className="onhold-details-remarks onhold-detail-field onhold-detail-field--wide">
          <strong>{remarksLabel || oh.recruitersRemarks}</strong>
          {(app.remarks || app.recruiter_remarks || app.recruiterNotes) && (() => {
            const remarksRaw = app.remarks || app.recruiter_remarks || app.recruiterNotes;
            const remarkEntries = parseSavedRemarkEntries(remarksRaw);
            return (
              <div className="saved-remarks-history">
                <span className="saved-remarks-preview-label">{ta.savedRemarksHistory}</span>
                {remarkEntries.length > 0 ? (
                  <div className="saved-remarks-list">
                    {remarkEntries.map((entry, index) => (
                      <div key={`remark-${app.id}-${index}`} className="saved-remark-entry">
                        <div className="saved-remark-entry-top">
                          {entry.date ? (
                            <time className="saved-remark-date" dateTime={entry.date}>
                              {formatSavedRemarkDate(entry.date, lang)}
                            </time>
                          ) : null}
                          {entry.meta ? (
                            <span className="saved-remark-meta">{entry.meta}</span>
                          ) : null}
                        </div>
                        {entry.body ? (
                          <p className="saved-remark-body">{entry.body}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <pre className="saved-remarks-preview">{remarksRaw}</pre>
                )}
              </div>
            );
          })()}
          <textarea
            className="remarks-input"
            rows="4"
            placeholder={remarksPlaceholder || ta.onHoldRemarksPlaceholder}
            value={getRemarksDraft(app)}
            onChange={(e) => handleNoteChange(app.id, e.target.value)}
          />
          <div className="remarks-save-row">
            <button
              type="button"
              className="btn-action-save-remarks"
              onClick={() => handleSaveRemarks(app.id)}
              disabled={!getRemarksDraft(app).trim()}
            >
              {ta.saveRemarks}
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (currentUser) {
      if (currentUser.position) setUserPosition(currentUser.position);
      setProfileName(currentUser.full_name || currentUser.name || '');
      setProfileUsername(currentUser.username || currentUser.email || '');
      setProfileAvatarFile(currentUser.avatar || null);
    }
  }, [currentUser]);

  useEffect(() => () => {
    if (avatarPreviewUrlRef.current) {
      URL.revokeObjectURL(avatarPreviewUrlRef.current);
      avatarPreviewUrlRef.current = null;
    }
    if (avatarCropUrlRef.current) {
      URL.revokeObjectURL(avatarCropUrlRef.current);
      avatarCropUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!profileFieldNotice) return undefined;
    const timer = window.setTimeout(() => {
      setProfileFieldNotice('');
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [profileFieldNotice]);

  const clearAvatarPreview = () => {
    if (avatarPreviewUrlRef.current) {
      URL.revokeObjectURL(avatarPreviewUrlRef.current);
      avatarPreviewUrlRef.current = null;
    }
    setProfileAvatarPreview(null);
  };

  const clearAvatarCropSource = () => {
    if (avatarCropUrlRef.current) {
      URL.revokeObjectURL(avatarCropUrlRef.current);
      avatarCropUrlRef.current = null;
    }
    setAvatarCropSrc(null);
  };

  const isHeadAdmin = isHeadAdminUser(currentUser);
  const isAdmin = isDepartmentAdminUser(currentUser);
  const canUseMessages = canAccessAdminMessages(currentUser);
  const canManageProfilePhoto = !isHeadAdmin;
  const canSeeScores = canViewAssessmentScores(currentUser);
  const storedAvatarUrl = profileAvatarFile
    ? `${buildAttachmentEmbedUrl(profileAvatarFile)}${profileAvatarVersion ? `&v=${profileAvatarVersion}` : ''}`
    : null;
  const profileAvatarUrl = profileAvatarPreview || storedAvatarUrl;

  const getAssessmentRecord = (app) => {
    const email = (app?.email || '').trim().toLowerCase();
    return email ? assessmentMap[email] : null;
  };

  const canSeeApplicantScore = (app) => {
    if (!canSeeScores) return false;
    if (isHeadAdmin) return true;
    return canDepartmentSeeApplication(app, currentUser);
  };

  const openApplicantDigitalResume = (app) => {
    openDigitalResumeTab(app, {
      viewResume: ta.viewResume,
      sections: ta.sections,
    });
  };

  const openApplicantFullDetails = (app) => {
    openFullDetailsTab(app, {
      labels: {
        reviewDetails: ta.reviewDetails,
        sections: ta.sections,
        attachments: ta.attachments,
        voiceScore: ta.voiceScore,
        noAssessmentYet: ta.noAssessmentYet,
        overallScore: ta.overallScore,
        englishConfidenceShort: ta.englishConfidenceShort,
        completedTrainingShort: ta.completedTrainingShort,
        exGreeting: ta.exGreeting,
        exEmpathy: ta.exEmpathy,
        exHold: ta.exHold,
        exClosing: ta.exClosing,
        assessmentNotes: ta.assessmentNotes,
      },
      attachmentDocs,
      assessmentRecord: getAssessmentRecord(app),
      canSeeScore: canSeeApplicantScore(app),
    });
  };

  const getScoreLabel = (score) => {
    const grade = getScoreGrade(score);
    if (grade === 'excellent') return ta.scoreExcellent;
    if (grade === 'good') return ta.scoreGood;
    if (grade === 'fair') return ta.scoreFair;
    return ta.scoreNeedsWork;
  };

  const renderVoiceScoreBadge = (score) => {
    if (score == null || Number.isNaN(Number(score))) return null;
    const numeric = Math.round(Number(score));
    return (
      <span className={`voice-score-badge voice-score-badge--${getScoreGrade(numeric)}`} title={getScoreLabel(numeric)}>
        {numeric}
      </span>
    );
  };

  const renderDashboardClose = (placement = 'card') => {
    if (!isHeadAdmin && !isAdmin) return null;

    const className = placement === 'hero'
      ? 'close-form-btn admin-hero-close'
      : placement === 'panel'
        ? 'close-form-btn panel-close-btn'
        : 'close-form-btn dashboard-close-btn';

    return (
      <button type="button" className={className} onClick={onBack} aria-label={tc.close}>
        &times;
      </button>
    );
  };

  const renderAdminPageHeading = (title, description, options = {}) => {
    const {
      variant = '',
      eyebrow = '',
      summary,
      summaryLabel,
      className = '',
    } = options;

    const variantClass = variant ? ` page-heading--admin-${variant}` : '';

    return (
      <div className={`page-heading page-heading--admin${variantClass} ${className}`.trim()}>
        <div className="page-heading-copy">
          {eyebrow ? <span className="admin-page-eyebrow">{eyebrow}</span> : null}
          <h1 className="admin-page-title">{title}</h1>
          {description ? <p className="admin-page-desc">{description}</p> : null}
        </div>
        {summary != null && (
          <div className="onhold-summary-card onhold-summary-card--compact">
            <span className="summary-value">{summary}</span>
            {summaryLabel ? <span className="summary-label">{summaryLabel}</span> : null}
          </div>
        )}
        {renderDashboardClose('panel')}
      </div>
    );
  };

  const renderPipelinePageHeading = (title, description, variant = '') => (
    renderAdminPageHeading(title, description, { variant })
  );

  const renderSidebarMenuItem = (item) => (
    <button
      key={item.id}
      type="button"
      className={`sidebar-menu-item menu-item ${activeTab === item.id ? 'active' : ''}`}
      onClick={() => navigateToTab(item.id)}
    >
      <SidebarNavIcon id={item.id} />
      <span className="menu-item-label">{item.label}</span>
    </button>
  );

  const renderMobileMenuItem = (item) => (
    <button
      key={item.id}
      type="button"
      className={`admin-mobile-menu-link${activeTab === item.id ? ' active' : ''}`}
      onClick={() => navigateToTab(item.id)}
    >
      <SidebarNavIcon id={item.id} className="admin-mobile-menu-icon" />
      <span className="admin-mobile-menu-label">{item.label}</span>
    </button>
  );

  const renderAdminUserProfile = (className = '', { showRole = true } = {}) => (
    <div className={`user-mini-profile${className ? ` ${className}` : ''}`}>
      <div className={`avatar${profileAvatarUrl ? ' avatar--photo' : ''}`} aria-hidden="true">
        {profileAvatarUrl ? (
          <img key={profileAvatarUrl} src={profileAvatarUrl} alt="" className="avatar-img" />
        ) : (
          userInitial
        )}
      </div>
      <div className="info">
        <p className="name">{displayName}</p>
        {showRole && (
          <p className="role">{isHeadAdmin ? ts.headAdmin : (userPosition || role)}</p>
        )}
      </div>
    </div>
  );

  const fetchJobDescriptions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api.php?action=get_job_descriptions`);
      const data = await response.json();
      if (data.status === 'success') {
        const map = {};
        (data.data || []).forEach((row) => {
          map[row.position_title] = row.description || '';
        });
        setJobDescriptions(map);
      }
    } catch (error) {
      console.error('Error fetching job descriptions:', error);
    }
  };

  const saveJobDescription = async () => {
    if (!selectedJobPosition) {
      setJobDescriptionNotice(ta.jobDescriptionSelectPosition);
      return;
    }

    setIsSavingJobDescription(true);
    setJobDescriptionNotice('');

    try {
      const response = await fetch(`${API_BASE_URL}/api.php?action=save_job_description`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          position_title: selectedJobPosition,
          description: jobDescriptionDraft
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setJobDescriptions((prev) => ({
          ...prev,
          [selectedJobPosition]: jobDescriptionDraft
        }));
        setJobDescriptionNotice(ta.jobDescriptionSaved);
        notifyJobDescriptionsUpdated();
      } else {
        setJobDescriptionNotice(data.message || ta.jobDescriptionSaveFailed);
      }
    } catch (error) {
      console.error('Error saving job description:', error);
      setJobDescriptionNotice(ta.jobDescriptionSaveFailed);
    } finally {
      setIsSavingJobDescription(false);
    }
  };

  const displayName = profileName || currentUser?.full_name || currentUser?.name || 'Administrator';
  const userInitial = displayName.charAt(0).toUpperCase();

  useEffect(() => {
    if (!isAllowedAdminTab(activeTab, currentUser)) {
      setActiveTab('app-review');
    }
  }, [activeTab, currentUser]);

  useEffect(() => {
    closeMobileMenu();
  }, [activeTab]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeMobileMenu();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;

      if (attachmentPreview) {
        event.stopPropagation();
        setAttachmentPreview(null);
        return;
      }

      if (changePasswordAdminId) {
        event.stopPropagation();
        closeChangePasswordModal();
        return;
      }

      if (editingAdminId) {
        event.stopPropagation();
        closeEditModal();
        return;
      }

      if (editingProfileField) {
        event.stopPropagation();
        setEditingProfileField(null);
      }
    };

    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [attachmentPreview, changePasswordAdminId, editingAdminId, editingProfileField]);

  useEffect(() => {
    if (activeTab === 'job-description' && isHeadAdmin) {
      fetchJobDescriptions();
    }
  }, [activeTab, isHeadAdmin]);

  useEffect(() => {
    if (!selectedJobPosition) {
      setJobDescriptionDraft('');
      return;
    }
    setJobDescriptionDraft(jobDescriptions[selectedJobPosition] || '');
  }, [selectedJobPosition, jobDescriptions]);

  const jobApplications = useMemo(
    () => filterSubmittedJobApplications(applications || []),
    [applications]
  );

  // Head Admin sees all; department interviewers only see applicants assigned to them
  const visibleApplications = jobApplications.filter((app) => {
    if (isHeadAdmin) return true;
    if (isAdmin) {
      if (!canDepartmentSeeApplication(app, currentUser)) return false;
      if (isDepartmentInterviewerUser(currentUser)) {
        const deptKey = getDepartmentKey(currentUser);
        if (isSharedQueueDepartment(deptKey)) return true;
        return isApplicationAssignedToUser(assignments, app.id, currentUser);
      }
      return true;
    }
    return true;
  });

  const reviewQueueApplications = visibleApplications.filter(isReviewQueueApplicant);

  // Head Admin + HR share the full messages inbox so both see who is chatting.
  const visibleMessages = useMemo(() => {
    const baseMessages = canUseMessages ? messages : [];
    return groupMessagesIntoConversations(filterDeletedMessages(baseMessages.filter(isChatMessage)));
  }, [messages, canUseMessages]);

  const unreadMessagesCount = useMemo(
    () => (canUseMessages ? countUnreadConversations(visibleMessages, 'admin') : 0),
    [canUseMessages, visibleMessages]
  );

  useEffect(() => {
    if (!canUseMessages) return;

    const nextFingerprint = collectChatFingerprint(visibleMessages);
    if (chatFingerprintRef.current === null) {
      chatFingerprintRef.current = nextFingerprint;
      return;
    }

    const alerts = findNewChatAlerts(chatFingerprintRef.current, visibleMessages, 'admin');
    chatFingerprintRef.current = nextFingerprint;

    if (!alerts.length || activeTab === 'messages') return;

    const latest = alerts[0];
    const more = alerts.length > 1 ? ` (+${alerts.length - 1})` : '';
    setChatToast({
      id: latest.id,
      title: `${formatT(ta.newApplicantChatTitle, { name: latest.name })}${more}`,
      body: latest.preview || ta.newApplicantChatBody,
    });
  }, [visibleMessages, canUseMessages, activeTab, ta.newApplicantChatTitle, ta.newApplicantChatBody]);

  useEffect(() => {
    if (activeTab === 'messages') {
      setChatToast(null);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!visibleMessages || visibleMessages.length === 0) {
      setSelectedMessageId(null);
      return;
    }

    const filtered = visibleMessages.filter((msg) => {
      const searchLower = searchQuery.trim().toLowerCase();
      if (!searchLower) return true;
      return (
        (msg.name || '').toLowerCase().includes(searchLower) ||
        (msg.email || '').toLowerCase().includes(searchLower)
      );
    });

    if (filtered.length === 0) {
      setSelectedMessageId(null);
      return;
    }

    if (!filtered.find((m) => String(m.id) === String(selectedMessageId))) {
      setSelectedMessageId(filtered[0].id);
    }
  }, [visibleMessages, selectedMessageId, searchQuery]);

  const statusCounts = visibleApplications.reduce((counts, app) => {
    const rawStatus = (app?.status || 'Other').trim();
    let key;

    if ([
      PIPELINE_STATUS.PENDING_HR,
      PIPELINE_STATUS.PENDING_OPS,
      PIPELINE_STATUS.PENDING_TL,
      PIPELINE_STATUS.PENDING_CLIENT,
      PIPELINE_STATUS.PENDING_HEAD_ADMIN,
      PIPELINE_STATUS.HIRED
    ].includes(rawStatus)) {
      key = rawStatus;
    } else if (/^\s*rejected\s*$/i.test(rawStatus)) {
      key = 'FAILED INTERVIEW';
    } else if (/^\s*(on\s*hold|onhold)\s*$/i.test(rawStatus)) {
      key = 'ONHOLD';
    } else if (/^\s*job\s*offer\s*$/i.test(rawStatus)) {
      key = 'JOB OFFER';
    } else if (/^\s*pooling\s*$/i.test(rawStatus)) {
      key = 'POOLING';
    } else if (/^\s*failed\s*interview\s*$/i.test(rawStatus)) {
      key = 'FAILED INTERVIEW';
    } else if (/^\s*passed\s*interview\s*$/i.test(rawStatus)) {
      key = 'PASSED INTERVIEW';
    } else if (/^\s*application\s*retracted\s*$/i.test(rawStatus)) {
      key = 'APPLICATION RETRACTED';
    } else {
      key = 'Other';
    }

    counts[key] = (counts[key] || 0) + 1;
    counts.total += 1;
    return counts;
  }, {
    'Pending HR': 0,
    'Pending Ops': 0,
    'Pending TL': 0,
    'Pending Client': 0,
    'Pending Head Admin': 0,
    Hired: 0,
    ONHOLD: 0,
    'JOB OFFER': 0,
    POOLING: 0,
    'PASSED INTERVIEW': 0,
    'FAILED INTERVIEW': 0,
    'APPLICATION RETRACTED': 0,
    Other: 0,
    total: 0
  });

  const onholdApplicants = jobApplications.filter(app => /^\s*(on\s*hold|onhold)\s*$/i.test((app.status || '').trim()));
  const jobOfferApplicants = jobApplications.filter(app => /^\s*job\s*offer\s*$/i.test((app.status || '').trim()));
  const hiredApplicants = jobApplications.filter(app => /^\s*hired\s*$/i.test((app.status || '').trim()));
  const poolingApplicants = jobApplications.filter(app => /^\s*pooling\s*$/i.test((app.status || '').trim()));
  const failedInterviewApplicants = jobApplications.filter((app) => isFailedInterviewStatus(app?.status));
  const passedInterviewApplicants = jobApplications.filter((app) => isPassedInterviewStatus(app?.status));
  const retractedApplicants = jobApplications.filter(app => /^\s*application\s*retracted\s*$/i.test((app.status || '').trim()));

  const filteredRetractedApplicants = useMemo(() => {
    const query = retractedSearchQuery.trim();
    if (!query) return retractedApplicants;
    if (!/^\d+$/.test(query)) return [];
    return retractedApplicants.filter((app) => String(app.application_no ?? '').includes(query));
  }, [retractedApplicants, retractedSearchQuery]);

  const documentedPositionsCount = useMemo(
    () => JOB_POSITION_OPTIONS.filter((position) => String(jobDescriptions[position] || '').trim().length > 0).length,
    [jobDescriptions]
  );

  const getAssignedApplicantsForInterviewer = (interviewer, departmentStatus) => (
    jobApplications.filter((app) => {
      if (isHiredApplicant(app) || isFailedInterviewStatus(app?.status) || isPassedInterviewStatus(app?.status)) return false;
      if ((app?.status || '').trim() !== departmentStatus) return false;
      return isApplicationAssignedToInterviewer(assignments, app.id, interviewer);
    })
  );

  const getAppsHandledByInterviewer = (interviewer, departmentKey) => (
    jobApplications.filter((app) => {
      const actor = getStageReviewer(app, departmentKey);
      return isSameReviewer(actor, interviewer);
    })
  );

  const getDepartmentStageActivity = (departmentKey, departmentStatus) => {
    const pending = [];
    const handled = [];

    jobApplications.forEach((app) => {
      if (isHiredApplicant(app)) return;
      const status = (app?.status || '').trim();
      const actor = getStageReviewer(app, departmentKey);

      if (status === departmentStatus) {
        pending.push({ app, actor });
        return;
      }

      if (actor?.name) {
        handled.push({ app, actor });
      }
    });

    handled.sort((a, b) => {
      const aTime = Date.parse(a.actor?.at || '') || 0;
      const bTime = Date.parse(b.actor?.at || '') || 0;
      return bTime - aTime;
    });

    return { pending, handled };
  };

  const formatActivityDate = (isoDate) => {
    if (!isoDate) return '';
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getAssignableApplicantsForInterviewer = (interviewer, departmentStatus, deptInterviewers) => (
    getPendingAppsForDepartment(departmentStatus).filter((app) => {
      if (isApplicationAssignedToInterviewer(assignments, app.id, interviewer)) return false;
      const assignedToOther = (deptInterviewers || []).some((member) => (
        member.id !== interviewer.id
        && isApplicationAssignedToInterviewer(assignments, app.id, member)
      ));
      return !assignedToOther;
    })
  );

  const handleConfirmAssignment = async (interviewer) => {
    if (!interviewer?.id || typeof onSaveAssignment !== 'function') return;
    const draftAppId = assignDraftByInterviewer[interviewer.id];
    if (!draftAppId) return;

    setAssignSavingId(interviewer.id);
    try {
      const result = await onSaveAssignment(draftAppId, interviewer.id, interviewer.email || '');
      if (result?.success) {
        setAssignDraftByInterviewer((prev) => ({ ...prev, [interviewer.id]: '' }));
        setOpenAssignInterviewerId(null);
      }
    } finally {
      setAssignSavingId(null);
    }
  };

  const getPendingAppsForDepartment = (status) => (
    jobApplications.filter((app) => (app?.status || '').trim() === status && !isHiredApplicant(app))
  );

  const fetchAdminUsers = async () => {
    try {
      const response = await fetch(`${API_URL}?action=get_admins`);
      const data = await response.json();

      if (Array.isArray(data)) {
        setAdminUsers(data);
        return;
      }

      if (data && data.status === 'success') {
        if (Array.isArray(data.data)) {
          setAdminUsers(data.data);
          return;
        }
        if (Array.isArray(data.admins)) {
          setAdminUsers(data.admins);
          return;
        }
      }

      if (data && Array.isArray(data.admins)) {
        setAdminUsers(data.admins);
        return;
      }

      setAdminUsers([]);
    } catch (error) {
      console.error("Error fetching admin users:", error);
      setAdminUsers([]);
    }
  };

  useEffect(() => {
    if ((isHeadAdmin || isAdmin) && typeof onFetchAssignments === 'function') {
      onFetchAssignments();
    }
  }, [isHeadAdmin, isAdmin, onFetchAssignments]);

  useEffect(() => {
    if (!canSeeScores) return undefined;

    let cancelled = false;

    const fetchAssessments = async () => {
      try {
        const response = await fetch(`${API_URL}?action=get_assessments`);
        const result = await response.json();
        if (!cancelled && result?.status === 'success' && result.data) {
          setAssessmentMap(result.data);
        }
      } catch (error) {
        console.error('Error fetching assessments:', error);
      }
    };

    fetchAssessments();
    return () => { cancelled = true; };
  }, [canSeeScores]);

  useEffect(() => {
    if (isHeadAdmin) {
      fetchAdminUsers();
    }
  }, [isHeadAdmin]);

  useEffect(() => {
    if (isHeadAdmin && (activeTab === 'interviewers' || activeTab === 'create-accounts')) {
      fetchAdminUsers();
    }
  }, [activeTab, isHeadAdmin]);

  useEffect(() => {
    if ((isHeadAdmin || isAdmin) && activeTab === 'messages') {
      fetchAdminUsers();
    }
  }, [activeTab, isHeadAdmin, isAdmin]);

  // Keep Interviewers / Registered Accounts in sync when current user changes their photo.
  useEffect(() => {
    if (!currentUser?.id) return;
    setAdminUsers((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((admin) => {
        if (Number(admin.id) !== Number(currentUser.id)) return admin;
        const nextAvatar = currentUser.avatar || null;
        if ((admin.avatar || null) === nextAvatar) return admin;
        changed = true;
        return { ...admin, avatar: nextAvatar };
      });
      return changed ? next : prev;
    });
  }, [currentUser?.id, currentUser?.avatar]);

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    const roleSelection = (newAdminRole || '').trim();
    const position = (newAdminPosition || '').trim();

    if (!newAdminName || !newAdminEmail || !newAdminPassword || !roleSelection || !position) {
      alert('Please fill in all admin account fields.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}?action=create_admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newAdminName,
          email: newAdminEmail,
          password: newAdminPassword,
          role: roleSelection,
          position
        })
      });
      const data = await response.json();
      alert(data.message || 'Admin account created.');
      if (data.status === 'success') {
        setNewAdminName('');
        setNewAdminEmail('');
        setNewAdminPassword('');
        setNewAdminPosition('');
        fetchAdminUsers();
      }
    } catch (error) {
      console.error('Error creating admin:', error);
      alert('There was an error creating the admin account.');
    }
  };

  const openEditModal = (admin) => {
    setEditingAdminId(admin.id);
    setEditAdminName(admin.name || admin.full_name || '');
    setEditAdminEmail(admin.email || '');
    setEditAdminPosition(admin.position || '');
  };

  const closeEditModal = () => {
    setEditingAdminId(null);
    setEditAdminName('');
    setEditAdminEmail('');
    setEditAdminPosition('');
  };

  const handleSaveEdit = async () => {
    if (!editAdminName || !editAdminEmail || !editAdminPosition) {
      alert('Kailangan i-fill ang Name, Email, at Position.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}?action=update_admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editingAdminId,
          name: editAdminName,
          position: editAdminPosition
        })
      });
      const data = await response.json();
      if (data.status !== 'success') {
        alert(data.message || 'Failed to update admin account.');
        return;
      }

      const emailResponse = await fetch(`${API_URL}?action=update_admin_profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editingAdminId,
          email: editAdminEmail
        })
      });
      const emailData = await emailResponse.json();
      alert(emailData.message || data.message || 'Admin updated successfully.');
      if (emailData.status === 'success') {
        closeEditModal();
        fetchAdminUsers();
      }
    } catch (error) {
      console.error('Error updating admin:', error);
      alert('There was an error updating the admin account.');
    }
  };

  const openChangePasswordModal = (admin) => {
    setChangePasswordAdminId(admin.id);
    setNewPassword('');
    setConfirmPassword('');
  };

  const closeChangePasswordModal = () => {
    setChangePasswordAdminId(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordNotification('');
    setShowChangePasswordNew(false);
    setShowChangePasswordConfirm(false);
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      setPasswordNotification('Kailangan i-fill ang password fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordNotification('Ang passwords ay hindi pareho.');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordNotification('Ang password ay dapat may minimum na 6 characters.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}?action=change_admin_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: changePasswordAdminId,
          password: newPassword
        })
      });
      const data = await response.json();
      alert(data.message || 'Password changed successfully.');
      if (data.status === 'success') {
        closeChangePasswordModal();
        fetchAdminUsers();
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('There was an error changing the password.');
    }
  };

  const startProfileFieldEdit = (field) => {
    setProfileFieldNotice('');
    setEditingProfileField(field);
    if (field === 'email') {
      setProfileEmailDraft(currentUser?.email || '');
    }
    if (field === 'username') {
      setProfileUsernameDraft(profileUsername || currentUser?.email || '');
    }
  };

  const cancelProfileFieldEdit = () => {
    setEditingProfileField(null);
    setProfileEmailDraft('');
    setProfileUsernameDraft('');
    setProfileFieldNotice('');
  };

  const saveProfileField = async (field) => {
    const nextEmail = (field === 'email' ? profileEmailDraft : profileUsernameDraft).trim();
    if (!nextEmail) {
      setProfileFieldNotice('Email / username is required.');
      return;
    }

    if (!currentUser?.id) {
      setProfileFieldNotice('Unable to update profile right now.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}?action=update_admin_profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: currentUser.id,
          email: nextEmail
        })
      });
      const data = await response.json();
      if (data.status !== 'success') {
        setProfileFieldNotice(data.message || 'Failed to update profile.');
        return;
      }

      setProfileUsername(nextEmail);
      if (typeof onProfileUpdate === 'function') {
        onProfileUpdate({ email: nextEmail, username: nextEmail });
      }
      cancelProfileFieldEdit();
      setProfileFieldNotice(data.message || 'Profile updated successfully.');
    } catch (error) {
      console.error('Error updating profile:', error);
      setProfileFieldNotice('Cannot connect to server.');
    }
  };

  const handleProfileAvatarChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !canManageProfilePhoto || !currentUser?.id) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const allowedExt = /\.(jpe?g|png|webp|gif)$/i;
    const typeOk = allowedTypes.includes(file.type);
    const extOk = allowedExt.test(file.name || '');
    if ((!typeOk && !extOk) || file.size > 2 * 1024 * 1024) {
      setProfileFieldNotice(ta.profilePhotoInvalid);
      return;
    }

    clearAvatarCropSource();
    const cropSrc = URL.createObjectURL(file);
    avatarCropUrlRef.current = cropSrc;
    setAvatarCropSrc(cropSrc);
    setProfileFieldNotice('');
  };

  const handleCancelAvatarCrop = () => {
    if (avatarUploading) return;
    clearAvatarCropSource();
  };

  const uploadAvatarBlob = async (blob) => {
    if (!canManageProfilePhoto || !currentUser?.id || !blob) return;

    const localPreview = URL.createObjectURL(blob);
    clearAvatarPreview();
    avatarPreviewUrlRef.current = localPreview;
    setProfileAvatarPreview(localPreview);

    const formData = new FormData();
    formData.append('id', String(currentUser.id));
    formData.append('avatar', blob, 'profile-avatar.jpg');

    setAvatarUploading(true);
    setProfileFieldNotice('');
    try {
      const response = await fetch(`${API_URL}?action=upload_admin_avatar`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (data.status !== 'success') {
        clearAvatarPreview();
        setProfileFieldNotice(data.message || ta.profilePhotoUploadFailed);
        return;
      }
      const nextAvatar = data.data?.avatar || null;
      setProfileAvatarFile(nextAvatar);
      setProfileAvatarVersion(Date.now());
      clearAvatarPreview();
      clearAvatarCropSource();
      setAdminUsers((prev) => (
        (prev || []).map((admin) => (
          Number(admin.id) === Number(currentUser.id)
            ? { ...admin, avatar: nextAvatar }
            : admin
        ))
      ));
      if (typeof onProfileUpdate === 'function') {
        onProfileUpdate({ avatar: nextAvatar });
      }
      setProfileFieldNotice(ta.profilePhotoUpdated);
    } catch (error) {
      console.error('Error uploading avatar:', error);
      clearAvatarPreview();
      setProfileFieldNotice(ta.profilePhotoUploadFailed);
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleConfirmAvatarCrop = async (blob) => {
    await uploadAvatarBlob(blob);
  };

  const handleRemoveProfileAvatar = async () => {
    if (!canManageProfilePhoto || !currentUser?.id || (!profileAvatarFile && !profileAvatarPreview)) return;

    setAvatarUploading(true);
    setProfileFieldNotice('');
    try {
      const response = await fetch(`${API_URL}?action=remove_admin_avatar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: currentUser.id }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        setProfileFieldNotice(data.message || ta.profilePhotoUploadFailed);
        return;
      }
      clearAvatarPreview();
      setProfileAvatarFile(null);
      setProfileAvatarVersion(Date.now());
      setAdminUsers((prev) => (
        (prev || []).map((admin) => (
          Number(admin.id) === Number(currentUser.id)
            ? { ...admin, avatar: null }
            : admin
        ))
      ));
      if (typeof onProfileUpdate === 'function') {
        onProfileUpdate({ avatar: null });
      }
      setProfileFieldNotice(ta.profilePhotoRemoved);
    } catch (error) {
      console.error('Error removing avatar:', error);
      setProfileFieldNotice(ta.profilePhotoUploadFailed);
    } finally {
      setAvatarUploading(false);
    }
  };

  const getInterviewDraftForApp = (app) => {
    if (!app?.id) return { date: '', time: '' };
    if (interviewDateInputs[app.id]) return interviewDateInputs[app.id];
    return splitInterviewDateParts(getAppField(app, ['interview_date'], ''));
  };

  const handleInterviewPartChange = (app, field, value) => {
    const appId = app?.id;
    if (!appId) return;

    setInterviewDateInputs((prev) => {
      const current = prev[appId] || splitInterviewDateParts(getAppField(app, ['interview_date'], ''));
      return {
        ...prev,
        [appId]: { ...current, [field]: value }
      };
    });
    setInterviewDateNotice((prev) => ({ ...prev, [appId]: null }));
  };

  const handleSaveInterviewDate = async (app) => {
    const appId = app?.id;
    const draft = getInterviewDraftForApp(app);
    const interviewDate = combineInterviewDateParts(draft.date, draft.time);
    if (!appId || !interviewDate) {
      setInterviewDateNotice((prev) => ({
        ...prev,
        [appId]: { type: 'error', text: ta.interviewDateRequired }
      }));
      return;
    }

    if (typeof onSaveInterviewDate !== 'function') {
      setInterviewDateNotice((prev) => ({
        ...prev,
        [appId]: { type: 'error', text: ta.interviewDateError }
      }));
      return;
    }

    if (interviewDateSaving[appId]) return;

    setInterviewDateSaving((prev) => ({ ...prev, [appId]: true }));
    setInterviewDateNotice((prev) => ({
      ...prev,
      [appId]: { type: 'pending', text: ta.interviewDateSaving }
    }));

    const scheduledBy = profileName || currentUser?.name || currentUser?.full_name || currentUser?.email || 'Admin';

    try {
      const result = await onSaveInterviewDate(appId, interviewDate, scheduledBy);

      setInterviewDateNotice((prev) => ({
        ...prev,
        [appId]: {
          type: result?.success ? 'success' : 'error',
          text: result?.success
            ? (result.emailSent ? (result.message || ta.interviewDateSaved) : ta.interviewDateSavedQuick)
            : (result?.message || ta.interviewDateError)
        }
      }));

      if (result?.success) {
        setInterviewDateInputs((prev) => ({ ...prev, [appId]: { ...draft } }));
      }
    } catch (error) {
      setInterviewDateNotice((prev) => ({
        ...prev,
        [appId]: { type: 'error', text: ta.interviewDateError }
      }));
    } finally {
      setInterviewDateSaving((prev) => ({ ...prev, [appId]: false }));
    }
  };

  const renderApplicantResumePanel = (app) => (
    <div className="resume-container">
      <div className="resume-header">
        <h3 className="resume-name">{app.first_name || 'No Name'} {app.last_name || ''}</h3>
        <div className="resume-contact">
          <span>Email: {app.email || 'N/A'}</span> |
          <span>Mobile Number: {app.mobile_number || 'N/A'}</span> |
          <span>City Municipality: {app.city_municipality || 'N/A'}</span>
        </div>
      </div>

      <div className="resume-section">
        <h4>{ta.sections.profExperience}</h4>
        <div className="resume-grid">
          <div><span>BPO Experience:</span> <strong>{app.bpo_experience || 'N/A'}</strong></div>
          <div><span>Latest Company:</span> <strong>{app.latest_company || 'None'}</strong></div>
          <div><span>Total Years Working:</span> <strong>{app.total_years_work || 'None'}</strong></div>
          <div><span>Relevant Years:</span> <strong>{app.relevant_years_work || 'None'}</strong></div>
          <div className="resume-full-width"><span>Reason for Leaving:</span> <strong>{app.reason_for_leaving || 'N/A'}</strong></div>
          <div className="resume-full-width">
            <EmploymentHistoryPanel app={app} lang={lang} />
          </div>
        </div>
      </div>

      <div className="resume-section">
        <h4>{ta.sections.eduBackground}</h4>
        <div className="resume-grid">
          <div className="resume-full-width"><span>Highest Attainment:</span> <strong>{app.highest_educ || 'N/A'}</strong></div>
          <div className="resume-full-width"><span>School Name:</span> <strong>{app.school_name || 'N/A'}</strong></div>
          <div><span>Year Graduated:</span> <strong>{app.year_graduated || 'N/A'}</strong></div>
        </div>
      </div>

      <div className="resume-section">
        <h4>{ta.sections.personalInfo}</h4>
        <div className="resume-grid">
          <div><span>Date of Birth:</span> <strong>{getAppField(app, ['dob', 'dateOfBirth'])}</strong></div>
          <div><span>Gender:</span> <strong>{app.gender || 'N/A'}</strong></div>
          <div><span>Civil Status:</span> <strong>{app.civil_status || 'N/A'}</strong></div>
          <div><span>Nationality:</span> <strong>{app.nationality || 'N/A'}</strong></div>
          <div className="resume-full-width"><span>Current Address:</span> <strong>{app.current_address || 'N/A'}</strong></div>
        </div>
      </div>

      <div className="resume-section">
        <h4>{ta.sections.emergency}</h4>
        <div className="resume-grid">
          <div><span>Contact Name:</span> <strong>{app.emergency_contact_name || 'N/A'}</strong></div>
          <div><span>Relationship:</span> <strong>{app.emergency_contact_relation || 'N/A'}</strong></div>
          <div><span>Contact Number:</span> <strong>{app.emergency_contact_number || 'N/A'}</strong></div>
        </div>
      </div>
    </div>
  );

  const renderFailedInterviewRemarks = (app) => {
    const failedReviewer = getFailedInterviewReviewer(app);
    const remarksText = (app.remarks || app.recruiter_remarks || app.recruiterNotes || '').trim();
    const remarkEntries = parseSavedRemarkEntries(remarksText);
    const reviewedAt = failedReviewer?.at
      ? new Date(failedReviewer.at).toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
      : '';

    return (
      <div className="failed-interview-remarks">
        <div className="failed-interview-remarks-header">
          <strong className="failed-interview-remarks-title">{ta.historyRemarks}</strong>
          {failedReviewer?.name && (
            <span className="failed-interview-remarks-meta">
              {ta.interviewedByLabel}: {failedReviewer.name}
              {reviewedAt ? ` · ${reviewedAt}` : ''}
            </span>
          )}
        </div>
        {remarksText ? (
          remarkEntries.length > 0 ? (
            <div className="saved-remarks-list">
              {remarkEntries.map((entry, index) => (
                <div key={`failed-remark-${app.id}-${index}`} className="saved-remark-entry">
                  <div className="saved-remark-entry-top">
                    {entry.date ? (
                      <time className="saved-remark-date" dateTime={entry.date}>
                        {formatSavedRemarkDate(entry.date, lang)}
                      </time>
                    ) : null}
                    {entry.meta ? (
                      <span className="saved-remark-meta">{entry.meta}</span>
                    ) : null}
                  </div>
                  {entry.body ? (
                    <p className="saved-remark-body">{entry.body}</p>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <pre className="failed-interview-remarks-text">{remarksText}</pre>
          )
        ) : (
          <p className="failed-interview-remarks-empty">{ta.noInterviewRemarks}</p>
        )}
      </div>
    );
  };

  const renderFailedInterviewResumeActions = (app) => {
    const resumePreviewItems = resolveAttachmentPreviewItems(app, attachmentDocs[0]);
    const firstResume = resumePreviewItems[0];
    if (!firstResume) return null;

    const resumeLabel = ta.attachments?.resume || 'Resume';

    return (
      <div className="failed-interview-resume-actions">
        <button
          type="button"
          className="btn-toggle-details btn-toggle-details--resume"
          onClick={() => openAttachmentViewer(firstResume.url, resumeLabel)}
        >
          {resumeLabel}
        </button>
      </div>
    );
  };

  const flaggedApplications = (visibleApplications || [])
    .map((app) => ({
      ...app,
      id: app.id,
      name: `${app.first_name || app.full_name || app.name || 'Applicant'} ${app.last_name || ''}`.trim(),
      email: resolveApplicantEmailFromApp(app),
      status: app.status || 'N/A',
      issues: getAppRequirementIssues(app),
    }))
    .filter((item) => item.issues.length > 0);

  // Send missing-documents notification via email (not in-app messages)
  const sendMissingDocumentNotification = async (app) => {
    const email = resolveApplicantEmailFromApp(app);

    const issuesList = formatRequirementLabels(app.issues);

    const messageText = `Hello ${app.name},\n\nThank you for submitting your application with Helport. We have reviewed your submission and found that the following required documents are missing or incomplete:\n\n${issuesList.map(issue => `• ${issue}`).join('\n')}\n\nTo proceed with your application, please log in to your account and update your application form with the correct information for the above items.\n\n⚠️ IMPORTANT: Your salary will be placed on hold until these required documents are properly submitted.\n\nIf you have already submitted this information and believe this is an error, or if you need any clarification, please reply to this message and we will be happy to assist you.\n\nBest regards,\nHelport HR Team`;

    try {
      // Send notification via API to backend
      const response = await fetch(`${API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'send_missing_document_notification',
          applicant_email: email,
          applicant_name: app.name,
          applicant_id: app.id,
          missing_documents: issuesList,
          message: messageText
        })
      });

      const result = await response.json();

      if (result.status === 'success' && result.email_sent) {
        const sentTo = result.resolved_email || email;
        alert(`✓ Email sent to ${app.name} (${sentTo}) about missing documents.`);
      } else {
        alert(`Failed to send email: ${result.message || 'Unknown error.'}${result.email_error ? `\n\nReason: ${result.email_error}` : ''}`);
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Failed to send email notification. Please try again.');
    }
  };

  const sendHiredRequirementsNotification = async (app, requirementLabels = []) => {
    const email = resolveApplicantEmailFromApp(app);
    if (!requirementLabels.length) {
      alert('No pending requirements to send.');
      return;
    }

    const applicantName = formatApplicantLegalName(app);
    const issuesList = requirementLabels;
    const messageText = `Hello ${applicantName},\n\nCongratulations on being hired at Helport!\n\nTo complete your onboarding, please submit the following onboarding requirement(s):\n\n${issuesList.map((issue) => `• ${issue}`).join('\n')}\n\nPlease log in to your Helport account and update your application with the required documents.\n\n⚠️ IMPORTANT: Your salary may be placed on hold until all onboarding requirements are completed.\n\nIf you need assistance, please reply to this message.\n\nBest regards,\nHelport HR Team`;

    try {
      const response = await fetch(`${API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'send_missing_document_notification',
          notification_type: 'hired_requirements',
          applicant_email: email,
          applicant_name: applicantName,
          applicant_id: app.id,
          missing_documents: issuesList,
          message: messageText,
        }),
      });

      const result = await response.json();

      if (result.status === 'success' && result.email_sent) {
        const sentTo = result.resolved_email || email;
        alert(`✓ Email sent to ${applicantName} (${sentTo}) about pending requirements.`);
      } else {
        alert(`Failed to send email: ${result.message || 'Unknown error.'}${result.email_error ? `\n\nReason: ${result.email_error}` : ''}`);
      }
    } catch (error) {
      console.error('Error sending hired requirements notification:', error);
      alert('Failed to send email notification. Please try again.');
    }
  };

  const handleRetractClick = (app) => {
    const applicantName = formatApplicantLegalName(app);
    const applicationNo = formatApplicationNo(app);
    const confirmed = window.confirm(
      formatT(ta.retractConfirm, { name: applicantName, no: applicationNo })
    );
    if (!confirmed) return;
    onRetractApplication?.(app.id);
  };

  return (
    <div className={`crm-dashboard-layout ${isHeadAdmin ? 'is-head-admin' : ''}`}>

      {/* ================= SIDEBAR COMPONENT ================= */}
      <aside className="crm-sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-top">
            <div className="sidebar-brand-mark" aria-hidden="true">H</div>
            <div className="sidebar-brand-copy">
              <h2 className="sidebar-brand-title">{ts.helport}</h2>
              <p className="sidebar-brand-subtitle">{ts.recruitmentHub}</p>
            </div>
          </div>
          <div className="sidebar-brand-meta">
            <span className={`role-badge ${isHeadAdmin ? 'role-badge--head' : ''}`}>
              <span className="role-badge-dot" aria-hidden="true" />
              {isHeadAdmin ? ts.headAdmin : (userPosition || tc.admin)}
            </span>
          </div>
        </div>

        <nav className="sidebar-menu">
          <div className="menu-group-label">{ts.menu}</div>
          {sidebarMenu.main
            .filter((item) => item.id !== 'messages' || canUseMessages)
            .map(renderSidebarMenuItem)}

          <div className="menu-group-label menu-group-label--spaced">{ts.settings}</div>
          {sidebarMenu.settings
            .filter((item) => !item.headAdminOnly || isHeadAdmin)
            .map(renderSidebarMenuItem)}

          {isHeadAdmin && (
            <>
              <div className="menu-group-label menu-group-label--spaced">{ts.adminControl}</div>
              {sidebarMenu.headAdmin.map(renderSidebarMenuItem)}
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          {typeof onLangChange === 'function' && (
            <div className="sidebar-lang-select">
              <label htmlFor="admin-lang-select">{tc.language}</label>
              <div className="sidebar-lang-select-control">
                <select
                  id="admin-lang-select"
                  value={lang}
                  onChange={(e) => onLangChange(e.target.value)}
                >
                  <option value="en">{tc.english}</option>
                  <option value="zh">{tc.chinese}</option>
                </select>
              </div>
            </div>
          )}

          {renderAdminUserProfile()}
        </div>
      </aside>

      <div
        className={`admin-mobile-menu-backdrop${isMobileMenuOpen ? ' open' : ''}`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      <aside
        className={`admin-mobile-menu-drawer${isMobileMenuOpen ? ' open' : ''}`}
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="admin-mobile-menu-header">
          <img src={hLogoImg} alt="Helport" className="admin-mobile-menu-logo" />
          <button
            type="button"
            className="admin-mobile-menu-close"
            onClick={closeMobileMenu}
            aria-label={tc.close}
          >
            &times;
          </button>
        </div>

        <nav className="admin-mobile-menu-nav">
          <div className="admin-mobile-menu-group-label">{ts.menu}</div>
          {sidebarMenu.main
            .filter((item) => item.id !== 'messages' || canUseMessages)
            .map(renderMobileMenuItem)}

          <div className="admin-mobile-menu-group-label admin-mobile-menu-group-label--spaced">{ts.settings}</div>
          {sidebarMenu.settings
            .filter((item) => !item.headAdminOnly || isHeadAdmin)
            .map(renderMobileMenuItem)}

          {isHeadAdmin && (
            <>
              <div className="admin-mobile-menu-group-label admin-mobile-menu-group-label--spaced">{ts.adminControl}</div>
              {sidebarMenu.headAdmin.map(renderMobileMenuItem)}
            </>
          )}
        </nav>

        {typeof onLangChange === 'function' && (
          <div className="admin-mobile-menu-lang">
            <span className="admin-mobile-menu-lang-label">{tc.language}</span>
            <div className="admin-mobile-menu-lang-options">
              <button
                type="button"
                className={lang === 'en' ? 'active' : ''}
                onClick={() => onLangChange('en')}
              >
                {tc.english}
              </button>
              <button
                type="button"
                className={lang === 'zh' ? 'active' : ''}
                onClick={() => onLangChange('zh')}
              >
                {tc.chinese}
              </button>
            </div>
          </div>
        )}

        <div className="admin-mobile-menu-footer">
          {renderAdminUserProfile('admin-mobile-menu-profile')}
        </div>
      </aside>

      {canUseMessages && activeTab !== 'messages' && (
        <MessagesFloatButton
          count={unreadMessagesCount}
          pulse={unreadMessagesCount > 0}
          onClick={() => setActiveTab('messages')}
          ariaLabel={getT(lang).common.messages}
        />
      )}

      <ChatToast
        open={Boolean(chatToast)}
        title={chatToast?.title}
        body={chatToast?.body}
        actionLabel={tc.messages}
        onAction={() => {
          setChatToast(null);
          setActiveTab('messages');
        }}
        onClose={() => setChatToast(null)}
      />

      {/* ================= MAIN CONTENT AREA ================= */}
      <main className={`crm-main-content${activeTab === 'job-description' ? ' crm-main-content--job-description-fixed' : ''}`} ref={mainContentRef}>
        <header className="admin-mobile-topbar">
          <img src={hLogoImg} alt="Helport" className="admin-mobile-topbar-logo" />
          <button
            type="button"
            className={`admin-mobile-menu-toggle${isMobileMenuOpen ? ' is-open' : ''}`}
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? tc.close : ts.menu}
            aria-expanded={isMobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </header>
        {/* TAB 1: APPLICANT REVIEW */}
        {activeTab === 'app-review' && (
          <div className="dashboard-container">
            <div className={`dashboard-card dashboard-card--review${isPhoneView ? ' dashboard-card--review-phone' : ''} has-panel-close`}>
              {renderDashboardClose()}

              {isHeadAdmin ? (
                <div className="admin-hero">
                  <div className="admin-hero-glow" aria-hidden="true" />
                  <div className="admin-hero-body">
                  <div className="admin-hero-content">
                    <span className="admin-hero-eyebrow">{ta.heroEyebrow}</span>
                    <h2 className="admin-hero-title">{ta.heroTitle}</h2>
                    <p className="admin-hero-subtitle">
                      {formatT(ta.heroSubtitle, { name: displayName })}
                    </p>
                  </div>
                  <div className="admin-hero-stats">
                    <div className="admin-hero-stat">
                      <span className="admin-hero-stat-value">{statusCounts.total}</span>
                      <span className="admin-hero-stat-label">{ta.totalApplicants}</span>
                    </div>
                    <div className="admin-hero-stat admin-hero-stat--accent">
                      <span className="admin-hero-stat-value">{
                        statusCounts['Pending HR']
                        + statusCounts['Pending Ops']
                        + statusCounts['Pending TL']
                        + statusCounts['Pending Client']
                        + statusCounts['Pending Head Admin']
                      }</span>
                      <span className="admin-hero-stat-label">{ta.inReview}</span>
                    </div>
                  </div>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="admin-title">{role.toUpperCase()} DASHBOARD</h2>
                  <p className="admin-welcome-text">Welcome back, {displayName}!</p>
                </>
              )}

              <div className={`section-box section-box--monitor${isMonitorExpanded ? ' is-expanded' : ''}`}>
                <div className="monitor-panel-header">
                  <div className="admin-section-heading-wrap">
                    <h3 className="admin-page-title admin-page-title--section">{ta.applicantMonitor}</h3>
                    <p className="admin-page-desc section-box-desc">{ta.monitorDesc}</p>
                  </div>
                  <button
                    type="button"
                    className="monitor-panel-toggle"
                    onClick={() => setIsMonitorExpanded((open) => !open)}
                    aria-expanded={isMonitorExpanded}
                    aria-controls="applicant-monitor-stats"
                    aria-label={isMonitorExpanded ? ta.hideMonitorStats : ta.showMonitorStats}
                  >
                    <span
                      className={`monitor-panel-arrow${isMonitorExpanded ? ' monitor-panel-arrow--down' : ' monitor-panel-arrow--up'}`}
                      aria-hidden="true"
                    />
                  </button>
                </div>

                <div className="monitor-panel-body" id="applicant-monitor-stats">
                  <div className="status-summary-grid">
                    {statCards.map(({ key, label, variant, icon, tab }) => (
                      <button
                        key={key}
                        type="button"
                        className={`status-card status-card--${variant} status-card--clickable`}
                        onClick={() => handleStatCardClick(tab)}
                        aria-label={`${label}: ${statusCounts[key] ?? 0}`}
                      >
                        <div className="status-card-top">
                          <span className={`status-card-icon status-card-icon--${variant}`} aria-hidden="true">{icon || null}</span>
                          <p className="status-card-title">{label}</p>
                        </div>
                        <p className="status-card-value">{statusCounts[key] ?? 0}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="section-box section-box--apps" ref={appsSectionRef}>
                <div className="section-box-header section-box-header--apps">
                  <div className="admin-section-heading-wrap">
                    <h3 className="admin-page-title admin-page-title--section">{ta.appsForReview}</h3>
                    <p className="admin-page-desc section-box-desc">{formatT(ta.queueCount, { n: reviewQueueApplications.length })}</p>
                  </div>
                </div>

                {/* INAYOS NA VISIBLE APPLICATIONS CHECK */}
                {(!reviewQueueApplications || reviewQueueApplications.length === 0) ? (
                  <p className="admin-no-apps">
                    {isAdmin && isDepartmentInterviewerUser(currentUser)
                      ? ta.noAssigned
                      : (isAdmin ? ta.noDepartmentQueue : ta.noPending)}
                  </p>
                ) : (
                  <div className="app-list-container">
                    {/* INAYOS NA VISIBLE APPLICATIONS MAP GAMIT ANG ?. */}
                    {reviewQueueApplications?.map(app => {
                      const viewerDeptKey = getDepartmentKey(currentUser);
                      const stageDeptKey = getDepartmentKeyForStatus(app.status) || viewerDeptKey;
                      const stageReviewer = getStageReviewer(app, stageDeptKey) || getCurrentStageReviewer(app, currentUser);
                      const atMyStage = (() => {
                        const required = getRequiredStatusForDepartment(viewerDeptKey);
                        return required ? (app.status || '').trim() === required : false;
                      })();
                      const usesAssignment = usesInterviewerAssignment(viewerDeptKey);
                      const claimedByOther = Boolean(
                        usesAssignment
                        && stageReviewer
                        && atMyStage
                        && !isSameReviewer(stageReviewer, currentUser)
                        && !isHeadAdmin
                      );
                      const appStatus = (app.status || '').trim();
                      const isAtClientInterview = appStatus === PIPELINE_STATUS.PENDING_CLIENT;
                      const isAtOperationsInterview = appStatus === PIPELINE_STATUS.PENDING_OPS;

                      return (
                      <div key={app.id} className="app-item-card">

                        <div className="app-item-header">
                          <div
                            className="app-item-identity app-item-identity--clickable"
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              const nextId = openAppCardId === app.id ? null : app.id;
                              setOpenAppCardId(nextId);
                              if (
                                nextId
                                && typeof onClaimReview === 'function'
                                && isAdmin
                                && !isHeadAdmin
                                && usesAssignment
                              ) {
                                onClaimReview(app.id);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                const nextId = openAppCardId === app.id ? null : app.id;
                                setOpenAppCardId(nextId);
                                if (
                                  nextId
                                  && typeof onClaimReview === 'function'
                                  && isAdmin
                                  && !isHeadAdmin
                                  && usesAssignment
                                ) {
                                  onClaimReview(app.id);
                                }
                              }
                            }}
                          >
                            <h4 className="app-item-title">
                              <span className="app-item-title-left">
                                {tc.applicationNo} <span className="app-item-number">{formatApplicationNo(app)}</span>
                              </span>
                              {isHeadAdmin && (
                                <button
                                  type="button"
                                  className="btn-app-no-pooling"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateStatus(app.id, 'Pooling');
                                  }}
                                  onKeyDown={(e) => e.stopPropagation()}
                                >
                                  {ta.statPooling}
                                </button>
                              )}
                            </h4>
                            <div className="app-item-summary">
                              <div className="app-item-summary-col app-item-summary-col--name">
                                <span className="app-item-summary-label">{tc.name}</span>
                                <p className="app-item-applicant">
                                  {app.first_name || tc.applicant} {app.last_name || ''}
                                  <span className={`app-item-caret${openAppCardId === app.id ? ' is-open' : ''}`} aria-hidden="true" />
                                </p>
                              </div>
                              <div className="app-item-summary-divider" aria-hidden="true" />
                              <div className="app-item-summary-col app-item-summary-col--segment">
                                <span className="app-item-summary-label">{ta.segment}</span>
                                <p className="app-item-summary-value">{app.position || tc['n/a']}</p>
                              </div>
                              <div className="app-item-summary-divider" aria-hidden="true" />
                              <div className="app-item-summary-col app-item-summary-col--status">
                                <span className="app-item-summary-label">{ta.assessmentStatus}</span>
                                <span className={`app-item-status app-status-pill ${getAppStatusClass(app.status)}`}>{app.status}</span>
                              </div>
                              {stageReviewer?.name && (
                                <>
                                  <div className="app-item-summary-divider" aria-hidden="true" />
                                  <div className="app-item-summary-col app-item-summary-col--interviewer">
                                    <span className="app-item-summary-label">{ta.interviewedByLabel}</span>
                                    <p className="app-item-summary-value app-item-reviewer-name">{stageReviewer.name}</p>
                                  </div>
                                </>
                              )}
                              {canSeeScores && (
                                <>
                                  <div className="app-item-summary-divider" aria-hidden="true" />
                                  <div className="app-item-summary-col app-item-summary-col--score">
                                    <span className="app-item-summary-label">{ta.voiceScore}</span>
                                    {canSeeApplicantScore(app) ? (
                                      getAssessmentRecord(app) ? (
                                        renderVoiceScoreBadge(getAssessmentRecord(app).overall_score)
                                      ) : (
                                        <span className="app-item-summary-muted">{ta.noAssessmentYet}</span>
                                      )
                                    ) : (
                                      <span className="app-item-summary-muted">—</span>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="app-item-actions">
                            {role !== 'Applicant' && (() => {
                              const canReview = canDepartmentReviewApplicant(app, currentUser, { isHeadAdmin }) && !claimedByOther;
                              const approveTarget = getApproveTargetStatus(currentUser, { isHeadAdmin });
                              const showClientActions = canReview && isAtClientInterview && viewerDeptKey === 'CLIENT';
                              const showOperationsActions = canReview && isAtOperationsInterview && viewerDeptKey === 'OPERATIONS';
                              const showStandardActions = canReview && !showClientActions && !showOperationsActions;

                              return (
                                <>
                                  {showClientActions && (
                                    <button onClick={() => handleUpdateStatus(app.id, PIPELINE_STATUS.PASSED_INTERVIEW)} className="btn-action-approve">{tc.approved}</button>
                                  )}
                                  {showOperationsActions && (
                                    <>
                                      <button onClick={() => handleUpdateStatus(app.id, PIPELINE_STATUS.PASSED_INTERVIEW)} className="btn-action-skip">{tc.skip}</button>
                                      {approveTarget && (
                                        <button onClick={() => handleUpdateStatus(app.id, approveTarget)} className="btn-action-approve">{tc.approve}</button>
                                      )}
                                    </>
                                  )}
                                  {showStandardActions && (
                                    <>
                                      <button onClick={() => handleUpdateStatus(app.id, 'Onhold')} className="btn-action-onhold">{tc.onhold}</button>
                                      {approveTarget && (
                                        <button onClick={() => handleUpdateStatus(app.id, approveTarget)} className="btn-action-approve">{tc.approve}</button>
                                      )}
                                    </>
                                  )}
                                  {canReview && (
                                    <button onClick={() => handleUpdateStatus(app.id, PIPELINE_STATUS.FAILED_INTERVIEW)} className="btn-action-failed-interview">{tc.failedInterview}</button>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {openAppCardId === app.id && (
                        <>
                        {(isHeadAdmin || isAdmin) && (() => {
                          const interviewDraft = getInterviewDraftForApp(app);
                          const savedInterviewDate = getAppField(app, ['interview_date'], '');
                          const hasSavedInterview = savedInterviewDate !== 'N/A' && savedInterviewDate !== '';

                          return (
                            <div className="interview-schedule-panel">
                              <div className="interview-schedule-header">
                                <span className="interview-schedule-label">{ta.interviewScheduleLabel}</span>
                              </div>

                              <div className="interview-schedule-controls">
                                <label className="interview-schedule-field interview-schedule-field--date">
                                  <span>{ta.interviewDateLabel}</span>
                                  <input
                                    type="date"
                                    className="interview-schedule-input"
                                    value={interviewDraft.date}
                                    onChange={(e) => handleInterviewPartChange(app, 'date', e.target.value)}
                                  />
                                </label>
                                <label className="interview-schedule-field interview-schedule-field--time">
                                  <span>{ta.interviewTimeLabel}</span>
                                  <input
                                    type="time"
                                    className="interview-schedule-input"
                                    value={interviewDraft.time}
                                    onChange={(e) => handleInterviewPartChange(app, 'time', e.target.value)}
                                  />
                                </label>
                                <button
                                  type="button"
                                  className="btn-set-interview-date"
                                  onClick={() => handleSaveInterviewDate(app)}
                                  disabled={Boolean(interviewDateSaving[app.id])}
                                  aria-busy={Boolean(interviewDateSaving[app.id])}
                                >
                                  {interviewDateSaving[app.id] ? ta.interviewDateSaving : ta.setInterviewDate}
                                </button>
                              </div>

                              {hasSavedInterview && (
                                <p className="interview-schedule-current">
                                  {formatT(ta.currentInterviewDate, {
                                    date: formatInterviewDate(savedInterviewDate, lang) || savedInterviewDate
                                  })}
                                </p>
                              )}

                              {interviewDateNotice[app.id] && (
                                <p className={`interview-schedule-notice interview-schedule-notice--${interviewDateNotice[app.id].type}`}>
                                  {interviewDateNotice[app.id].text}
                                </p>
                              )}
                            </div>
                          );
                        })()}

                        <div className="app-details-wrapper">
                          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }} className="app-detail-actions">
                            <button
                              type="button"
                              onClick={() => openApplicantDigitalResume(app)}
                              className="btn-toggle-details"
                            >
                              {ta.viewResume}
                            </button>

                            <button
                              type="button"
                              onClick={() => openApplicantFullDetails(app)}
                              className="btn-toggle-details"
                              style={{ backgroundColor: '#28a745' }}
                            >
                              {ta.reviewDetails}
                            </button>
                          </div>

                          {!isHeadAdmin && (
                            <div className="app-textarea-wrapper" style={{ marginTop: '18px' }}>
                              <label className="notes-label">Recruiter / Interviewer Remarks</label>
                              <textarea
                                className="notes-textarea"
                                placeholder={ta.remarksPlaceholder}
                                value={notesInputs[app.id] || ''}
                                onChange={(e) => handleNoteChange(app.id, e.target.value)}
                              ></textarea>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginTop: '10px' }}>
                                <button
                                  type="button"
                                  className="btn-action-save-remarks"
                                  onClick={() => handleSaveRemarks(app.id)}
                                  disabled={!notesInputs[app.id] || !notesInputs[app.id].trim()}
                                >
                                  {ta.saveRemarks}
                                </button>
                                {app.remarks && (
                                  <span className="saved-remarks-preview">Saved: {app.remarks}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {app.remarks && (
                          <div className="app-remarks-history">
                            <strong className="app-remarks-label">History / Remarks:</strong>
                            <pre className="app-remarks-text">{app.remarks}</pre>
                          </div>
                        )}
                        </>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>

              
            </div>
          </div>
        )}

        {canUseMessages && activeTab === 'messages' && (
          <div className="tab-container messages-page admin-messages">
            <MessagesChat
              lang={lang}
              title={tc.messages}
              subtitle={ta.messagesSubtitleAdmin}
              messages={visibleMessages}
              selectedMessageId={selectedMessageId}
              onSelectMessage={setSelectedMessageId}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              replyInput={replyInputs}
              onReplyChange={handleReplyChange}
              onSubmitReply={handleSubmitReply}
              mode="admin"
              currentUserName={profileName || currentUser?.full_name || currentUser?.name || ''}
              searchPlaceholder={ta.searchApplicants}
              emptyListMessage={ta.noMessagesAdmin}
              emptySearchMessage={ta.noSearchMatch}
              emptySelectionMessage={ta.selectMessage}
              onDeleteMessage={onDeleteMessage}
              onEditChatMessage={onEditChatMessage}
              onDeleteChatMessage={onDeleteChatMessage}
              hiddenMessageIds={hiddenMessageIds}
              adminUsers={adminUsers}
              onClose={onBack}
            />
          </div>
        )}

        {activeTab === 'create-accounts' && isHeadAdmin && (
          <div className="tab-container admin-tab-panel admin-tab-panel--polished admin-tab-panel--create-accounts has-panel-close">
            {renderAdminPageHeading(ta.createAdminTitle, ta.createAdminDesc, {
              summary: adminUsers.length,
              summaryLabel: ta.registeredAccounts,
            })}

            <div className="create-accounts-layout">
              <div className="create-accounts-columns">
                <div className="create-accounts-card create-accounts-card--form">
                  <div className="create-accounts-card-head">
                    <h2 className="create-accounts-card-title">{ta.createDepartmentAccount}</h2>
                    <p className="create-accounts-card-desc">{ta.createAdminDesc}</p>
                  </div>
                  <form onSubmit={handleCreateAdmin} className="create-admin-form">
                    <label className="create-admin-field">
                      <span>{tc.name}</span>
                      <input
                        type="text"
                        className="create-admin-input"
                        value={newAdminName}
                        onChange={(e) => setNewAdminName(e.target.value)}
                        placeholder={tc.name}
                      />
                    </label>
                    <label className="create-admin-field">
                      <span>{tc.email}</span>
                      <input
                        type="email"
                        className="create-admin-input"
                        value={newAdminEmail}
                        onChange={(e) => setNewAdminEmail(e.target.value)}
                        placeholder={tc.email}
                      />
                    </label>
                    <label className="create-admin-field">
                      <span>{ta.password}</span>
                      <div className="create-admin-password-wrap">
                        <input
                          type={showCreateAdminPassword ? 'text' : 'password'}
                          className="create-admin-input"
                          value={newAdminPassword}
                          onChange={(e) => setNewAdminPassword(e.target.value)}
                          placeholder={ta.password}
                        />
                        <button
                          type="button"
                          className={`account-settings-icon-btn account-settings-icon-btn--eye${showCreateAdminPassword ? ' is-visible' : ''}`}
                          aria-label={showCreateAdminPassword ? tc.hidePassword : tc.showPassword}
                          onClick={() => setShowCreateAdminPassword((prev) => !prev)}
                        />
                      </div>
                    </label>
                    <div className="create-admin-field-row">
                      <label className="create-admin-field">
                        <span>{ta.selectRole}</span>
                        <select
                          className="create-admin-input create-admin-select"
                          value={newAdminRole}
                          onChange={(e) => setNewAdminRole(e.target.value)}
                        >
                          <option value="HEAD ADMIN">{ts.headAdmin}</option>
                          <option value="ADMIN">{tc.admin}</option>
                        </select>
                      </label>
                      <label className="create-admin-field">
                        <span>{ta.position}</span>
                        <select
                          className="create-admin-input create-admin-select"
                          value={newAdminPosition}
                          onChange={(e) => setNewAdminPosition(e.target.value)}
                        >
                          <option value="">{ta.selectPositionDept}</option>
                          <option value="HR Department">HR Department</option>
                          <option value="HR Intern">HR Intern</option>
                          <option value="Operations">Operations</option>
                          <option value="Client">Client</option>
                          <option value="Team Leader">Team Leader</option>
                        </select>
                      </label>
                    </div>
                    <button type="submit" className="create-admin-submit-btn">
                      {ta.createAdminAccountBtn}
                    </button>
                  </form>
                </div>

                <div className="create-accounts-card create-accounts-card--registered">
                  <div className="create-accounts-card-head create-accounts-card-head--inline">
                    <div>
                      <h2 className="create-accounts-card-title">{ta.registeredAccounts}</h2>
                      <p className="create-accounts-card-desc">
                        {formatT(ta.registeredAccountsCount, { n: adminUsers.length })}
                      </p>
                    </div>
                    <div className="create-accounts-count-pill">{adminUsers.length}</div>
                  </div>

                  {adminUsers.length > 0 && (
                    <label className="registered-accounts-search" htmlFor="registered-accounts-search-input">
                      <span className="registered-accounts-search-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="7" />
                          <path d="M20 20l-3.5-3.5" />
                        </svg>
                      </span>
                      <input
                        id="registered-accounts-search-input"
                        type="search"
                        value={registeredAccountsSearch}
                        onChange={(e) => setRegisteredAccountsSearch(e.target.value)}
                        placeholder={ta.searchRegisteredAccounts}
                        autoComplete="off"
                      />
                      {registeredAccountsSearch ? (
                        <button
                          type="button"
                          className="registered-accounts-search-clear"
                          onClick={() => setRegisteredAccountsSearch('')}
                          aria-label="Clear search"
                        >
                          ×
                        </button>
                      ) : null}
                    </label>
                  )}

                  {(() => {
                    const searchQ = registeredAccountsSearch.trim().toLowerCase();
                    const filteredAccounts = [...(adminUsers || [])]
                      .filter((admin) => {
                        if (!searchQ) return true;
                        const hay = `${admin.name || ''} ${admin.full_name || ''} ${admin.email || ''} ${admin.position || ''} ${admin.role || ''}`.toLowerCase();
                        return hay.includes(searchQ);
                      })
                      .sort((a, b) => {
                        const an = (a.name || a.full_name || a.email || '').toLowerCase();
                        const bn = (b.name || b.full_name || b.email || '').toLowerCase();
                        return an.localeCompare(bn);
                      });

                    if (adminUsers.length === 0) {
                      return <div className="registered-accounts-empty">{ta.noRegisteredAccounts}</div>;
                    }

                    if (filteredAccounts.length === 0) {
                      return <div className="registered-accounts-empty">{ta.registeredAccountsSearchEmpty}</div>;
                    }

                    return (
                      <div className="registered-accounts-list">
                        {filteredAccounts.map((admin) => {
                          const adminName = admin.name || admin.full_name || admin.email || tc.admin;
                          const adminInitial = adminName.charAt(0).toUpperCase();
                          const position = (admin.position || '').toLowerCase();
                          const deptClass = position.includes('hr')
                            ? 'hr'
                            : position.includes('oper')
                              ? 'ops'
                              : position.includes('team')
                                ? 'tl'
                                : position.includes('client')
                                  ? 'client'
                                  : 'default';

                          return (
                            <div key={admin.id} className="registered-account-item">
                              <PersonAvatar
                                admin={admin}
                                className={`registered-account-avatar registered-account-avatar--${deptClass}`}
                                initial={adminInitial}
                              />
                              <div className="registered-account-info">
                                <div className="registered-account-topline">
                                  <p className="registered-account-name">{adminName}</p>
                                  <span className={`registered-account-dept registered-account-dept--${deptClass}`}>
                                    {(admin.role || '').toUpperCase() === 'HEAD ADMIN' ? ts.headAdmin : (admin.role || tc.admin)}
                                    {admin.position ? ` · ${admin.position}` : ''}
                                  </span>
                                </div>
                                <p className="registered-account-email">{admin.email}</p>
                              </div>
                              <div className="registered-account-actions">
                                <button
                                  type="button"
                                  onClick={() => openEditModal(admin)}
                                  className="registered-account-btn registered-account-btn--primary"
                                >
                                  {tc.edit}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openChangePasswordModal(admin)}
                                  className="registered-account-btn registered-account-btn--secondary"
                                >
                                  {ta.changePasswordBtn}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {editingAdminId && (
              <div className="admin-modal-overlay" onClick={closeEditModal}>
                <div className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
                  <h2 className="admin-modal-title">{ta.editAdminTitle}</h2>
                  <div className="admin-modal-form">
                    <label className="create-admin-field">
                      <span>{tc.name}</span>
                      <input
                        type="text"
                        className="create-admin-input"
                        value={editAdminName}
                        onChange={(e) => setEditAdminName(e.target.value)}
                        placeholder={tc.name}
                      />
                    </label>
                    <label className="create-admin-field">
                      <span>{tc.email}</span>
                      <input
                        type="email"
                        className="create-admin-input"
                        value={editAdminEmail}
                        onChange={(e) => setEditAdminEmail(e.target.value)}
                        placeholder={tc.email}
                      />
                    </label>
                    <label className="create-admin-field">
                      <span>{ta.position}</span>
                      <select
                        className="create-admin-input create-admin-select"
                        value={editAdminPosition}
                        onChange={(e) => setEditAdminPosition(e.target.value)}
                      >
                        <option value="">{ta.selectPositionDept}</option>
                        <option value="HR Department">HR Department</option>
                        <option value="HR Intern">HR Intern</option>
                        <option value="Operations">Operations</option>
                        <option value="Client">Client</option>
                        <option value="Team Leader">Team Leader</option>
                        {editAdminPosition
                          && !['HR Department', 'HR Intern', 'Operations', 'Client', 'Team Leader', ''].includes(editAdminPosition) ? (
                          <option value={editAdminPosition}>{editAdminPosition}</option>
                        ) : null}
                      </select>
                    </label>
                    <div className="admin-modal-actions">
                      <button type="button" onClick={handleSaveEdit} className="create-admin-submit-btn">
                        {ta.saveChanges}
                      </button>
                      <button type="button" onClick={closeEditModal} className="account-settings-cancel-btn">
                        {tc.cancel}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {changePasswordAdminId && (
              <div className="admin-modal-overlay" onClick={closeChangePasswordModal}>
                <div className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
                  <h2 className="admin-modal-title">{ta.changePasswordTitle}</h2>
                  {passwordNotification && (
                    <div className="admin-modal-error">{passwordNotification}</div>
                  )}
                  <div className="admin-modal-form">
                    <label className="create-admin-field">
                      <span>{ta.newPassword}</span>
                      <div className="create-admin-password-wrap">
                        <input
                          type={showChangePasswordNew ? 'text' : 'password'}
                          className="create-admin-input"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder={ta.newPassword}
                        />
                        <button
                          type="button"
                          className={`account-settings-icon-btn account-settings-icon-btn--eye${showChangePasswordNew ? ' is-visible' : ''}`}
                          aria-label={showChangePasswordNew ? tc.hidePassword : tc.showPassword}
                          onClick={() => setShowChangePasswordNew((prev) => !prev)}
                        />
                      </div>
                    </label>
                    <label className="create-admin-field">
                      <span>{ta.confirmPassword}</span>
                      <div className="create-admin-password-wrap">
                        <input
                          type={showChangePasswordConfirm ? 'text' : 'password'}
                          className="create-admin-input"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder={ta.confirmPassword}
                        />
                        <button
                          type="button"
                          className={`account-settings-icon-btn account-settings-icon-btn--eye${showChangePasswordConfirm ? ' is-visible' : ''}`}
                          aria-label={showChangePasswordConfirm ? tc.hidePassword : tc.showPassword}
                          onClick={() => setShowChangePasswordConfirm((prev) => !prev)}
                        />
                      </div>
                    </label>
                    <div className="admin-modal-actions">
                      <button type="button" onClick={handleChangePassword} className="create-admin-submit-btn">
                        {ta.changePasswordBtn}
                      </button>
                      <button type="button" onClick={closeChangePasswordModal} className="account-settings-cancel-btn">
                        {tc.cancel}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'interviewers' && isHeadAdmin && (
          <div className="tab-container admin-tab-panel admin-tab-panel--polished admin-tab-panel--interviewers has-panel-close">
            {renderAdminPageHeading(ta.interviewerTitle, ta.interviewerDescAuto, {
              variant: 'interviewers',
              summary: adminUsers.length,
              summaryLabel: ta.teamMembers,
            })}

            <div className="interviewer-toolbar">
              <label className="interviewer-search" htmlFor="interviewer-search-input">
                <span className="interviewer-search-icon" aria-hidden="true">⌕</span>
                <input
                  id="interviewer-search-input"
                  type="search"
                  value={interviewerSearch}
                  onChange={(e) => setInterviewerSearch(e.target.value)}
                  placeholder={ta.searchInterviewers}
                  autoComplete="off"
                />
              </label>
            </div>

            <div className="interviewer-board">
              {INTERVIEWER_DEPT_CONFIG.map(({ key, slug, labelKey, departmentKey, status, sharedQueue }) => {
                const searchQ = interviewerSearch.trim().toLowerCase();
                const deptMembers = (adminUsers || []).filter((admin) => {
                  const p = (admin.position || '').toLowerCase();
                  if (key === 'HR') return p.includes('hr');
                  if (key === 'Operations') return p.includes('oper');
                  if (key === 'Team Leader') return p.includes('team leader') || p.includes('team lead');
                  if (key === 'Client') return p.includes('client');
                  return false;
                });
                const list = deptMembers
                  .filter((admin) => {
                    if (!searchQ) return true;
                    const hay = `${admin.name || ''} ${admin.full_name || ''} ${admin.email || ''} ${admin.position || ''}`.toLowerCase();
                    return hay.includes(searchQ);
                  })
                  .sort((a, b) => {
                    const an = (a.name || a.full_name || a.email || '').toLowerCase();
                    const bn = (b.name || b.full_name || b.email || '').toLowerCase();
                    return an.localeCompare(bn);
                  });
                const deptLabel = ta[labelKey] || key;
                const pendingInDept = getPendingAppsForDepartment(status);
                const stageActivity = getDepartmentStageActivity(departmentKey, status);
                const pendingForActivity = sharedQueue
                  ? stageActivity.pending
                  : stageActivity.pending.filter(({ app }) => getAssignmentEntries(assignments, app.id).length === 0);
                const activityItems = [
                  ...pendingForActivity.map((item) => ({ ...item, kind: 'pending' })),
                  ...stageActivity.handled.slice(0, 8).map((item) => ({ ...item, kind: 'handled' })),
                ];

                return (
                  <section key={key} className={`interviewer-dept-column interviewer-dept-column--${slug}`}>
                    <header className="interviewer-dept-header">
                      <div className="interviewer-dept-head">
                        <h3 className="interviewer-dept-title">{deptLabel}</h3>
                        <span className="interviewer-dept-count">
                          {searchQ ? `${list.length}/${deptMembers.length}` : deptMembers.length}
                        </span>
                      </div>
                      <div className="interviewer-dept-meta">
                        <span className={`interviewer-dept-pending${pendingInDept.length > 0 ? ' is-hot' : ''}`}>
                          {formatT(ta.deptQueueCount, { n: pendingInDept.length })}
                        </span>
                      </div>
                    </header>

                    <div className="interviewer-dept-body">
                      {list.length === 0 ? (
                        <div className="interviewer-dept-empty">{ta.noInterviewersInDept}</div>
                      ) : (
                        <div className="interviewer-card-list">
                          {list.map((a) => {
                            const interviewerName = a.name || a.full_name || a.email;
                            const interviewerInitial = (interviewerName || 'U')[0].toUpperCase();
                            const positionLabel = a.position || tc.noPosition;

                            if (sharedQueue) {
                              const handledCount = getAppsHandledByInterviewer(a, departmentKey).length;
                              return (
                                <article key={a.id} className="interviewer-card interviewer-card--shared">
                                  <div className="interviewer-card-top">
                                    <PersonAvatar
                                      admin={a}
                                      className={`interviewer-card-avatar interviewer-card-avatar--${slug}`}
                                      initial={interviewerInitial}
                                    />
                                    <div className="interviewer-card-info">
                                      <h4 className="interviewer-card-name">{interviewerName}</h4>
                                      <p className="interviewer-card-role">{positionLabel}</p>
                                      <p className="interviewer-card-email" title={a.email}>{a.email}</p>
                                    </div>
                                    {handledCount > 0 && (
                                      <span className="interviewer-card-stat">
                                        {formatT(ta.interviewedApplicantsCount, { n: handledCount })}
                                      </span>
                                    )}
                                  </div>
                                </article>
                              );
                            }

                            const assignedApps = getAssignedApplicantsForInterviewer(a, status);
                            const assignableApps = getAssignableApplicantsForInterviewer(a, status, deptMembers);
                            const isAssignOpen = openAssignInterviewerId === a.id;
                            const assignDraft = assignDraftByInterviewer[a.id] || '';

                            return (
                              <article key={a.id} className={`interviewer-card${isAssignOpen ? ' is-expanded' : ''}`}>
                                <div className="interviewer-card-top">
                                  <PersonAvatar
                                    admin={a}
                                    className={`interviewer-card-avatar interviewer-card-avatar--${slug}`}
                                    initial={interviewerInitial}
                                  />
                                  <div className="interviewer-card-info">
                                    <h4 className="interviewer-card-name">{interviewerName}</h4>
                                    <p className="interviewer-card-role">{positionLabel}</p>
                                    <p className="interviewer-card-email" title={a.email}>{a.email}</p>
                                  </div>
                                  <div className="interviewer-card-actions">
                                    {assignedApps.length > 0 && (
                                      <span className="interviewer-card-stat interviewer-card-stat--assigned">
                                        {formatT(ta.assignedApplicantsCount, { n: assignedApps.length })}
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      className={`interviewer-assign-btn${isAssignOpen ? ' is-active' : ''}`}
                                      onClick={() => {
                                        setOpenAssignInterviewerId(isAssignOpen ? null : a.id);
                                        setAssignDraftByInterviewer((prev) => ({ ...prev, [a.id]: prev[a.id] || '' }));
                                      }}
                                    >
                                      {ta.assignShort}
                                    </button>
                                  </div>
                                </div>

                                {(assignedApps.length > 0 || isAssignOpen) && (
                                  <div className="interviewer-card-footer">
                                    {assignedApps.length > 0 && (
                                      <div className="interviewer-assigned-list">
                                        {assignedApps.map((app) => (
                                          <div key={`assigned-${a.id}-${app.id}`} className="interviewer-assigned-item">
                                            <span className="interviewer-assigned-name">
                                              {app.first_name || app.name || tc.applicant} {app.last_name || ''}
                                            </span>
                                            <span className="interviewer-assigned-meta">
                                              {formatApplicationNo(app)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {isAssignOpen && (
                                      <div className="interviewer-assign-panel">
                                        <label className="interviewer-assign-label" htmlFor={`assign-select-${a.id}`}>
                                          {ta.assignApplicantLabel}
                                        </label>
                                        <select
                                          id={`assign-select-${a.id}`}
                                          className="interviewer-assign-select"
                                          value={assignDraft}
                                          onChange={(e) => {
                                            setAssignDraftByInterviewer((prev) => ({
                                              ...prev,
                                              [a.id]: e.target.value,
                                            }));
                                          }}
                                        >
                                          <option value="">{ta.selectApplicant}</option>
                                          {assignableApps.map((app) => (
                                            <option key={app.id} value={app.id}>
                                              {formatApplicationNo(app)} — {app.first_name || tc.applicant} {app.last_name || ''} ({app.position || ta.positionNotSpecified})
                                            </option>
                                          ))}
                                        </select>
                                        {assignableApps.length === 0 && (
                                          <p className="interviewer-assign-hint">{ta.noAssignableApplicants}</p>
                                        )}
                                        <div className="interviewer-assign-actions">
                                          <button
                                            type="button"
                                            className="interviewer-assign-confirm"
                                            onClick={() => handleConfirmAssignment(a)}
                                            disabled={!assignDraft || assignSavingId === a.id}
                                          >
                                            {assignSavingId === a.id ? ta.savingAssignment : tc.confirm}
                                          </button>
                                          <button
                                            type="button"
                                            className="interviewer-assign-cancel"
                                            onClick={() => {
                                              setOpenAssignInterviewerId(null);
                                              setAssignDraftByInterviewer((prev) => ({ ...prev, [a.id]: '' }));
                                            }}
                                          >
                                            {tc.cancel}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </article>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <details className="interviewer-dept-activity" defaultOpen={activityItems.length > 0}>
                      <summary className="interviewer-dept-activity-summary">
                        <span>{ta.recentMoves}</span>
                        <span className="interviewer-dept-activity-count">{activityItems.length}</span>
                      </summary>
                      {activityItems.length === 0 ? (
                        <p className="interviewer-dept-activity-empty">{ta.noStageActivityYet}</p>
                      ) : (
                        <div className="interviewer-dept-activity-list">
                          {activityItems.map(({ app, actor, kind }) => (
                            <div
                              key={`activity-${departmentKey}-${kind}-${app.id}`}
                              className={`interviewer-dept-activity-item${kind === 'handled' ? ' is-handled' : ''}`}
                            >
                              <div className="interviewer-dept-activity-main">
                                <div className="interviewer-dept-activity-name">
                                  {app.first_name || tc.applicant} {app.last_name || ''}
                                </div>
                                <div className="interviewer-dept-activity-meta">
                                  {formatApplicationNo(app)}
                                </div>
                              </div>
                              <div className="interviewer-dept-activity-footer">
                                {kind === 'pending' && !actor?.name ? (
                                  <span className="interviewer-activity-awaiting">{ta.awaitingAction}</span>
                                ) : (
                                  <span className="interviewer-activity-handler">
                                    {formatT(ta.handledBy, { name: actor?.name || tc['n/a'] })}
                                  </span>
                                )}
                                {actor?.at ? (
                                  <time className="interviewer-activity-date" dateTime={actor.at}>
                                    {formatActivityDate(actor.at)}
                                  </time>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </details>
                  </section>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'onhold' && isHeadAdmin && (
          <div className="tab-container pipeline-stage-page pipeline-stage-page--onhold">
            {renderPipelinePageHeading(ta.onHoldTitle, ta.onHoldDesc, 'onhold')}

            {onholdApplicants.length === 0 ? (
              <PipelineEmptyState variant="onhold" message={ta.onHoldEmpty} />
            ) : (
              <div className="onhold-list">
                {onholdApplicants.map((app) => (
                  <article key={app.id} className="onhold-card pipeline-applicant-card pipeline-applicant-card--onhold">
                    <PipelineApplicantHeader
                      name={formatApplicantLegalName(app)}
                      position={getAppField(app, ['position', 'role'], ta.positionNotSpecified)}
                      statusLabel={app.status ? app.status.replace(/onhold/i, 'On Hold') : 'On Hold'}
                      stageVariant="onhold"
                    />

                    {renderApplicantStageDetails(app)}

                    <div className="pooling-account-section">
                      <p className="pooling-account-label">
                        {ta.assignedAccountLabel}
                        <span className="pooling-account-current">{app.assigned_account || ta.accountUnassigned}</span>
                      </p>
                      <div className="pooling-account-actions">
                        {POOLING_ACCOUNT_OPTIONS.map((accountName) => {
                          const isActive = String(app.assigned_account || '').toLowerCase() === accountName.toLowerCase();
                          return (
                            <button
                              key={accountName}
                              type="button"
                              className={`btn-pooling-account${isActive ? ' is-active' : ''}`}
                              onClick={() => onAssignAccount?.(app.id, accountName)}
                            >
                              {accountName}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="onhold-card-actions">
                      <button type="button" onClick={() => handleUpdateStatus(app.id, 'Job Offer')} className="btn-action-joboffer">{ta.issueJobOffer}</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'job-offer' && isHeadAdmin && (
          <div className="tab-container pipeline-stage-page pipeline-stage-page--job-offer">
            {renderPipelinePageHeading(ta.jobOfferTitle, ta.jobOfferDesc, 'job-offer')}

            {jobOfferApplicants.length === 0 ? (
              <PipelineEmptyState variant="job-offer" message={ta.jobOfferEmpty} />
            ) : (
              <div className="section-box section-box--apps">
                <div className="app-list-container job-offer-app-list">
                  {jobOfferApplicants.map((app) => (
                    <div key={app.id} className="app-item-card app-item-card--job-offer">
                      <div className="app-item-header">
                        <div
                          className="app-item-identity app-item-identity--clickable"
                          role="button"
                          tabIndex={0}
                          aria-expanded={openJobOfferCardId === app.id}
                          onClick={() => setOpenJobOfferCardId(openJobOfferCardId === app.id ? null : app.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setOpenJobOfferCardId(openJobOfferCardId === app.id ? null : app.id);
                            }
                          }}
                        >
                          <h4 className="app-item-title">
                            <span className="app-item-title-left">
                              {tc.applicationNo} <span className="app-item-number">{formatApplicationNo(app)}</span>
                            </span>
                          </h4>
                          <div className="app-item-summary app-item-summary--job-offer">
                            <div className="app-item-summary-col app-item-summary-col--name">
                              <span className="app-item-summary-label">{tc.name}</span>
                              <p className="app-item-applicant">
                                {[app.first_name, app.middle_name, app.last_name].filter(Boolean).join(' ') || tc.applicant}
                                <span className={`app-item-caret${openJobOfferCardId === app.id ? ' is-open' : ''}`} aria-hidden="true" />
                              </p>
                            </div>
                            <div className="app-item-summary-divider" aria-hidden="true" />
                            <div className="app-item-summary-col app-item-summary-col--segment">
                              <span className="app-item-summary-label">{ta.segment}</span>
                              <p className="app-item-summary-value">{app.position || tc['n/a']}</p>
                            </div>
                            <div className="app-item-summary-divider" aria-hidden="true" />
                            <div className="app-item-summary-col app-item-summary-col--status">
                              <span className="app-item-summary-label">{ta.assessmentStatus}</span>
                              <span className="app-item-status app-status-pill app-status-pill--job-offer">{ta.statJobOffer}</span>
                            </div>
                          </div>
                        </div>

                        <div className="app-item-actions job-offer-actions">
                          <button
                            type="button"
                            onClick={() => handleUpdateStatus(app.id, 'Hired')}
                            className="btn-action-hired"
                          >
                            {ta.markHired}
                          </button>
                        </div>
                      </div>

                      {openJobOfferCardId === app.id && (
                        <div className="job-offer-expanded">
                          {renderApplicantStageDetails(app, {
                            remarksLabel: ta.recruitersFinalRemarks,
                            remarksPlaceholder: ta.jobOfferRemarksPlaceholder
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'hired' && isHeadAdmin && (
          <div className="tab-container pipeline-stage-page pipeline-stage-page--hired">
            {renderPipelinePageHeading(ta.hiredTitle, ta.hiredDesc, 'hired')}

            {hiredApplicants.length === 0 ? (
              <PipelineEmptyState variant="hired" message={ta.hiredEmpty} />
            ) : (
              <div className="onhold-list">
                {hiredApplicants.map((app) => {
                  const requirementStatus = getHiredRequirementStatus(app);
                  const pendingRequirements = getPendingHiredRequirements(app);
                  const pendingLabels = pendingRequirements.map((item) => item.label);

                  return (
                  <article key={app.id} className="onhold-card pipeline-applicant-card pipeline-applicant-card--hired hired-card">
                    <div
                      className="hired-card-toggle"
                      role="button"
                      tabIndex={0}
                      onClick={() => setOpenHiredCardId(openHiredCardId === app.id ? null : app.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOpenHiredCardId(openHiredCardId === app.id ? null : app.id);
                        }
                      }}
                    >
                      <p className="hired-card-name">
                        {formatApplicantLegalName(app)}
                        <span className={`app-item-caret${openHiredCardId === app.id ? ' is-open' : ''}`} aria-hidden="true" />
                      </p>
                      <div className="hired-card-toggle-actions">
                        {pendingRequirements.length > 0 && (
                          <span className="hired-card-pending-badge">
                            {formatT(ta.hiredPendingCount, { n: pendingRequirements.length })}
                          </span>
                        )}
                        <button
                          type="button"
                          className="btn-action-retracted btn-action-retracted--compact"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRetractClick(app);
                          }}
                        >
                          {ta.markRetracted}
                        </button>
                      </div>
                    </div>

                    {openHiredCardId === app.id && (
                    <div className="hired-card-details">
                      <div className="hired-requirements-panel">
                        <div className="hired-requirements-header">
                          <div>
                            <h4 className="hired-requirements-title">{ta.hiredRequirementsTitle}</h4>
                            <p className="hired-requirements-desc">{ta.hiredRequirementsDesc}</p>
                          </div>
                          {pendingRequirements.length > 0 && (
                            <button
                              type="button"
                              className="btn-action-hired hired-requirements-send-all"
                              onClick={() => sendHiredRequirementsNotification(app, pendingLabels)}
                            >
                              {ta.sendAllPendingRequirements}
                            </button>
                          )}
                        </div>

                        <div className="hired-requirements-list">
                          {requirementStatus.map((item) => (
                            <div
                              key={item.key}
                              className={`hired-requirement-item${item.complete ? ' is-complete' : ' is-pending'}`}
                            >
                              <div className="hired-requirement-main">
                                <span className="hired-requirement-label">{item.label}</span>
                                <span className={`hired-requirement-status${item.complete ? ' is-complete' : ' is-pending'}`}>
                                  {item.complete ? ta.requirementSubmitted : ta.requirementPending}
                                </span>
                              </div>
                              {!item.complete && (
                                <button
                                  type="button"
                                  className="hired-requirement-notify-btn"
                                  onClick={() => sendHiredRequirementsNotification(app, [item.label])}
                                >
                                  {ta.sendRequirementNotice}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {renderApplicantStageDetails(app, {
                        remarksLabel: ta.recruitersFinalRemarks,
                        remarksPlaceholder: ta.hiredRemarksPlaceholder
                      })}
                    </div>
                    )}

                  </article>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'pooling' && isHeadAdmin && (
          <div className="tab-container pipeline-stage-page pipeline-stage-page--pooling">
            {renderPipelinePageHeading(ta.poolingTitle, ta.poolingDesc, 'pooling')}

            {poolingApplicants.length === 0 ? (
              <PipelineEmptyState variant="pooling" message={ta.poolingEmpty} />
            ) : (
              <div className="onhold-list">
                {poolingApplicants.map((app) => (
                  <article key={app.id} className="onhold-card pipeline-applicant-card pipeline-applicant-card--pooling pooling-card">
                    <div
                      className="hired-card-toggle pooling-card-toggle"
                      role="button"
                      tabIndex={0}
                      onClick={() => setOpenPoolingCardId(openPoolingCardId === app.id ? null : app.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOpenPoolingCardId(openPoolingCardId === app.id ? null : app.id);
                        }
                      }}
                    >
                      <p className="hired-card-name">
                        {formatApplicantLegalName(app)}
                        <span className={`app-item-caret${openPoolingCardId === app.id ? ' is-open' : ''}`} aria-hidden="true" />
                      </p>
                    </div>

                    {openPoolingCardId === app.id && (
                      <div className="pooling-card-details">
                        <PipelineApplicantHeader
                          name={formatApplicantLegalName(app)}
                          position={getAppField(app, ['position', 'role'], ta.positionNotSpecified)}
                          statusLabel={app.status || ta.statPooling}
                          stageVariant="pooling"
                        />

                        {renderApplicantStageDetails(app)}

                        <div className="pooling-account-section">
                          <p className="pooling-account-label">
                            {ta.assignedAccountLabel}
                            <span className="pooling-account-current">{app.assigned_account || ta.accountUnassigned}</span>
                          </p>
                          <div className="pooling-account-actions">
                            {POOLING_ACCOUNT_OPTIONS.map((accountName) => {
                              const isActive = String(app.assigned_account || '').toLowerCase() === accountName.toLowerCase();
                              return (
                                <button
                                  key={accountName}
                                  type="button"
                                  className={`btn-pooling-account${isActive ? ' is-active' : ''}`}
                                  onClick={() => onAssignAccount?.(app.id, accountName)}
                                >
                                  {accountName}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="onhold-card-actions">
                          <button type="button" onClick={() => handleUpdateStatus(app.id, 'Job Offer')} className="btn-action-joboffer">{ta.issueJobOffer}</button>
                        </div>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'passed-interview' && isHeadAdmin && (
          <div className="tab-container pipeline-stage-page pipeline-stage-page--passed-interview">
            {renderPipelinePageHeading(ta.passedInterviewTitle, ta.passedInterviewDesc, 'passed-interview')}

            {passedInterviewApplicants.length === 0 ? (
              <PipelineEmptyState variant="passed-interview" message={ta.passedInterviewEmpty} />
            ) : (
              <div className="section-box section-box--apps">
                <div className="app-list-container passed-interview-app-list">
                  {passedInterviewApplicants.map((app) => (
                    <div key={app.id} className="app-item-card app-item-card--passed-interview">
                      <div className="app-item-header">
                        <div
                          className="app-item-identity app-item-identity--clickable"
                          role="button"
                          tabIndex={0}
                          onClick={() => setOpenPassedCardId(openPassedCardId === app.id ? null : app.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setOpenPassedCardId(openPassedCardId === app.id ? null : app.id);
                            }
                          }}
                        >
                          <h4 className="app-item-title">
                            <span className="app-item-title-left">
                              {tc.applicationNo} <span className="app-item-number">{formatApplicationNo(app)}</span>
                            </span>
                          </h4>
                          <div className="app-item-summary app-item-summary--passed-interview">
                            <div className="app-item-summary-col app-item-summary-col--name">
                              <span className="app-item-summary-label">{tc.name}</span>
                              <p className="app-item-applicant">
                                {[app.first_name, app.middle_name, app.last_name].filter(Boolean).join(' ') || tc.applicant}
                                <span className={`app-item-caret${openPassedCardId === app.id ? ' is-open' : ''}`} aria-hidden="true" />
                              </p>
                            </div>
                            <div className="app-item-summary-divider" aria-hidden="true" />
                            <div className="app-item-summary-col app-item-summary-col--segment">
                              <span className="app-item-summary-label">{ta.segment}</span>
                              <p className="app-item-summary-value">{app.position || tc['n/a']}</p>
                            </div>
                            <div className="app-item-summary-divider" aria-hidden="true" />
                            <div className="app-item-summary-col app-item-summary-col--status">
                              <span className="app-item-summary-label">{ta.assessmentStatus}</span>
                              <span className="app-item-status app-status-pill app-status-pill--passed-interview">{ta.statusPassed}</span>
                            </div>
                          </div>
                        </div>

                        <div className="app-item-actions passed-interview-actions">
                          <button
                            type="button"
                            onClick={() => handleUpdateStatus(app.id, 'Job Offer')}
                            className="btn-action-joboffer"
                          >
                            {ts.jobOffer}
                          </button>
                        </div>
                      </div>

                      {openPassedCardId === app.id && (
                        <div className="passed-interview-expanded">
                          {renderFailedInterviewResumeActions(app)}
                          {renderFailedInterviewRemarks(app)}
                          {renderApplicantResumePanel(app)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'failed-interview' && isHeadAdmin && (
          <div className="tab-container pipeline-stage-page pipeline-stage-page--failed-interview">
            {renderPipelinePageHeading(ta.failedInterviewTitle, ta.failedInterviewDesc, 'failed-interview')}

            {failedInterviewApplicants.length === 0 ? (
              <PipelineEmptyState variant="failed-interview" message={ta.failedInterviewEmpty} />
            ) : (
              <div className="section-box section-box--apps">
                <div className="app-list-container failed-interview-app-list">
                  {failedInterviewApplicants.map((app) => (
                    <div key={app.id} className="app-item-card app-item-card--failed-interview">
                      <div className="app-item-header">
                        <div
                          className="app-item-identity app-item-identity--clickable"
                          role="button"
                          tabIndex={0}
                          onClick={() => setOpenFailedCardId(openFailedCardId === app.id ? null : app.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setOpenFailedCardId(openFailedCardId === app.id ? null : app.id);
                            }
                          }}
                        >
                          <h4 className="app-item-title">
                            <span className="app-item-title-left">
                              {tc.applicationNo} <span className="app-item-number">{formatApplicationNo(app)}</span>
                            </span>
                          </h4>
                          <div className="app-item-summary app-item-summary--failed-interview">
                            <div className="app-item-summary-col app-item-summary-col--name">
                              <span className="app-item-summary-label">{tc.name}</span>
                              <p className="app-item-applicant">
                                {[app.first_name, app.middle_name, app.last_name].filter(Boolean).join(' ') || tc.applicant}
                                <span className={`app-item-caret${openFailedCardId === app.id ? ' is-open' : ''}`} aria-hidden="true" />
                              </p>
                            </div>
                            <div className="app-item-summary-divider" aria-hidden="true" />
                            <div className="app-item-summary-col app-item-summary-col--segment">
                              <span className="app-item-summary-label">{ta.segment}</span>
                              <p className="app-item-summary-value">{app.position || tc['n/a']}</p>
                            </div>
                            <div className="app-item-summary-divider" aria-hidden="true" />
                            <div className="app-item-summary-col app-item-summary-col--status">
                              <span className="app-item-summary-label">{ta.assessmentStatus}</span>
                              <span className="app-item-status app-status-pill app-status-pill--failed-interview">{ta.statusFailed}</span>
                            </div>
                          </div>
                        </div>

                        {canReturnFailedInterviewToReview(app) && (
                          <div className="app-item-actions failed-interview-actions">
                            <button
                              type="button"
                              onClick={() => {
                                const returnStatus = getFailedInterviewReturnStatus(app);
                                if (returnStatus) {
                                  handleUpdateStatus(app.id, returnStatus);
                                  setOpenFailedCardId(null);
                                }
                              }}
                              className="btn-action-return-interview"
                            >
                              {ta.returnToInterview}
                            </button>
                          </div>
                        )}
                      </div>

                      {openFailedCardId === app.id && (
                        <div className="failed-interview-expanded">
                          {renderFailedInterviewResumeActions(app)}
                          {renderFailedInterviewRemarks(app)}
                          {renderApplicantResumePanel(app)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'application-retracted' && isHeadAdmin && (
          <div className="tab-container pipeline-stage-page pipeline-stage-page--retracted">
            {renderPipelinePageHeading(ta.retractedTitle, ta.retractedDesc, 'retracted')}

            <div className="retracted-search-bar">
              <label className="retracted-search" htmlFor="retracted-search-input">
                <span className="retracted-search-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-3.5-3.5" />
                  </svg>
                </span>
                <input
                  id="retracted-search-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="retracted-search-input"
                  placeholder={ta.searchApplicationNo}
                  value={retractedSearchQuery}
                  onChange={(e) => setRetractedSearchQuery(e.target.value.replace(/\D/g, ''))}
                  autoComplete="off"
                />
                {retractedSearchQuery ? (
                  <button
                    type="button"
                    className="retracted-search-clear"
                    onClick={() => setRetractedSearchQuery('')}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                ) : null}
              </label>
            </div>

            {retractedApplicants.length === 0 ? (
              <PipelineEmptyState variant="retracted" message={ta.retractedEmpty} />
            ) : filteredRetractedApplicants.length === 0 ? (
              <PipelineEmptyState variant="retracted" message={ta.retractedSearchEmpty} />
            ) : (
              <div className="onhold-list">
                {filteredRetractedApplicants.map((app) => (
                  <article key={app.id} className="onhold-card pipeline-applicant-card pipeline-applicant-card--retracted retracted-record-card">
                    <div className="retracted-record-row">
                      <div className="retracted-record-main">
                        <p className="retracted-record-name">{formatApplicantLegalName(app)}</p>
                        <p className="retracted-record-no">
                          {tc.applicationNo}{' '}
                          <span className="retracted-record-number">{formatApplicationNo(app)}</span>
                        </p>
                      </div>
                      <span className="status-pill status-pill--retracted">{ta.statRetracted}</span>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'app-requirements-guard' && isHeadAdmin && (
          <div className="tab-container pipeline-stage-page pipeline-stage-page--guard">
            {renderPipelinePageHeading(ta.guardTitle, ta.guardDesc, 'guard')}

            {flaggedApplications.length === 0 ? (
              <PipelineEmptyState variant="guard" message={ta.guardEmpty} />
            ) : (
              <div className="onhold-list">
                {flaggedApplications.map((app) => (
                  <article key={app.id} className="onhold-card onhold-card--guard pipeline-applicant-card pipeline-applicant-card--guard guard-card">
                    <div
                      className="hired-card-toggle guard-card-toggle"
                      role="button"
                      tabIndex={0}
                      aria-expanded={openGuardCardId === app.id}
                      onClick={() => setOpenGuardCardId(openGuardCardId === app.id ? null : app.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOpenGuardCardId(openGuardCardId === app.id ? null : app.id);
                        }
                      }}
                    >
                      <p className="hired-card-name">
                        {app.name}
                        <span className={`app-item-caret${openGuardCardId === app.id ? ' is-open' : ''}`} aria-hidden="true" />
                      </p>
                    </div>

                    {openGuardCardId === app.id && (
                      <div className="guard-card-details">
                        <PipelineApplicantHeader
                          name={app.name}
                          position={app.status || ta.positionNotSpecified}
                          statusLabel={ta.flagged}
                          stageVariant="guard"
                        />

                        <div className="onhold-card-grid">
                          <div><strong>{ta.emailAddress}</strong> {app.email || tc['n/a']}</div>
                          <div><strong>{ta.missingDocuments}</strong> {app.issues.join(', ')}</div>
                        </div>

                        <div className="onhold-card-actions guard-card-actions">
                          <button
                            type="button"
                            onClick={() => sendMissingDocumentNotification(app)}
                            className="btn-action-joboffer onhold-guard-notify-btn"
                            disabled={!app.id}
                            title={!app.email ? 'Send email notification (email will be looked up from application records).' : 'Send email notification to applicant'}
                          >
                            {ta.sendNotification}
                          </button>
                        </div>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'job-description' && isHeadAdmin && (
          <div className="tab-container admin-tab-panel admin-tab-panel--job-description has-panel-close">
            <div className="job-description-page">
              {renderAdminPageHeading(ta.jobDescriptionTitle, ta.jobDescriptionDesc, {
                variant: 'job-description',
                eyebrow: ta.jobDescriptionEyebrow,
                summary: `${documentedPositionsCount}/${JOB_POSITION_OPTIONS.length}`,
                summaryLabel: ta.jobDescriptionDocumented,
                className: 'job-description-heading',
              })}

              <div className="job-description-layout">
                <aside className="job-description-aside">
                  <div className="job-description-guide-card">
                    <div className="job-description-guide-icon" aria-hidden="true" />
                    <h3>{ta.jobDescriptionGuideTitle}</h3>
                    <ul className="job-description-guide-list">
                      <li>{ta.jobDescriptionGuide1}</li>
                      <li>{ta.jobDescriptionGuide2}</li>
                      <li>{ta.jobDescriptionGuide3}</li>
                      <li>{ta.jobDescriptionGuide4}</li>
                    </ul>
                  </div>
                </aside>

                <section className="job-description-editor-card">
                  <div className="job-description-editor-head">
                    <h2 className="job-description-section-title">{ta.jobDescriptionEditorTitle}</h2>
                    <p className="job-description-section-desc">{ta.jobDescriptionEditorDesc}</p>
                  </div>

                  <div className="job-description-form-grid">
                    <div className="job-description-field job-description-field--position">
                      <label htmlFor="job-description-position">{ta.jobDescriptionPosition}</label>
                      <div className="job-description-select-wrap">
                        <select
                          id="job-description-position"
                          className="job-description-select"
                          value={selectedJobPosition}
                          onChange={(e) => setSelectedJobPosition(e.target.value)}
                        >
                          <option value="">{ta.jobDescriptionChoosePosition}</option>
                          {JOB_POSITION_OPTIONS.map((position) => (
                            <option key={position} value={position}>{position}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="job-description-field job-description-field--content">
                      <div className="job-description-field-header">
                        <label htmlFor="job-description-content">{ta.jobDescriptionContent}</label>
                        {selectedJobPosition && (
                          <span className="job-description-char-count">{jobDescriptionDraft.length}</span>
                        )}
                      </div>
                      <div className={`job-description-editor-wrap${selectedJobPosition ? '' : ' is-disabled'}`}>
                        <textarea
                          id="job-description-content"
                          className="job-description-textarea"
                          placeholder={
                            selectedJobPosition
                              ? ta.jobDescriptionPlaceholder
                              : ta.jobDescriptionSelectPositionHint
                          }
                          value={jobDescriptionDraft}
                          onChange={(e) => setJobDescriptionDraft(e.target.value)}
                          disabled={!selectedJobPosition}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="job-description-footer">
                    <div className="job-description-footer-meta">
                      {selectedJobPosition ? (
                        <span className="job-description-active-position">{selectedJobPosition}</span>
                      ) : (
                        <span className="job-description-footer-hint">{ta.jobDescriptionChoosePosition}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="job-description-save-btn"
                      onClick={saveJobDescription}
                      disabled={!selectedJobPosition || isSavingJobDescription}
                    >
                      {isSavingJobDescription ? ta.jobDescriptionSaving : ta.jobDescriptionSave}
                    </button>
                  </div>

                  {jobDescriptionNotice && (
                    <p className="job-description-notice">{jobDescriptionNotice}</p>
                  )}
                </section>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="tab-container admin-tab-panel admin-tab-panel--profile has-panel-close">
            <div className="account-settings-page">
              {renderAdminPageHeading(ta.accountSettings, ta.accountSettingsDesc, {
                variant: 'account',
                className: 'account-settings-header',
              })}

              <div className="account-settings-layout">
                <aside className="account-settings-profile-panel">
                  <div className="account-settings-hero account-settings-hero--vertical">
                    <div className={`account-settings-avatar-wrap${canManageProfilePhoto ? ' is-editable' : ''}`}>
                      {canManageProfilePhoto && (
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                          className="account-settings-avatar-input"
                          onChange={handleProfileAvatarChange}
                        />
                      )}
                      {canManageProfilePhoto ? (
                        <button
                          type="button"
                          className={`account-settings-avatar account-settings-avatar--large${profileAvatarUrl ? ' has-photo' : ''} is-clickable`}
                          aria-label={profileAvatarUrl ? ta.changeProfilePhoto : ta.uploadProfilePhoto}
                          disabled={avatarUploading}
                          onClick={() => avatarInputRef.current?.click()}
                        >
                          {profileAvatarUrl ? (
                            <img key={profileAvatarUrl} src={profileAvatarUrl} alt="" className="account-settings-avatar-img" />
                          ) : (
                            userInitial
                          )}
                        </button>
                      ) : (
                        <div
                          className={`account-settings-avatar account-settings-avatar--large${profileAvatarUrl ? ' has-photo' : ''}`}
                          aria-hidden="true"
                        >
                          {profileAvatarUrl ? (
                            <img key={profileAvatarUrl} src={profileAvatarUrl} alt="" className="account-settings-avatar-img" />
                          ) : (
                            userInitial
                          )}
                        </div>
                      )}
                      {canManageProfilePhoto && (
                        <div className="account-settings-avatar-actions">
                          <button
                            type="button"
                            className="account-settings-avatar-btn"
                            disabled={avatarUploading}
                            onClick={() => avatarInputRef.current?.click()}
                          >
                            {avatarUploading
                              ? '...'
                              : (profileAvatarUrl ? ta.changeProfilePhoto : ta.uploadProfilePhoto)}
                          </button>
                          {profileAvatarUrl && (
                            <button
                              type="button"
                              className="account-settings-avatar-btn account-settings-avatar-btn--ghost"
                              disabled={avatarUploading}
                              onClick={handleRemoveProfileAvatar}
                            >
                              {ta.removeProfilePhoto}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="account-settings-hero-body">
                      <h2>{profileName || tc.admin}</h2>
                      <p>{currentUser?.email || '—'}</p>
                      <span className={`account-settings-role-pill ${isHeadAdmin ? 'account-settings-role-pill--head' : ''}`}>
                        {isHeadAdmin ? ts.headAdmin : (userPosition || tc.admin)}
                      </span>
                    </div>
                  </div>

                  <div className="account-settings-meta-grid">
                    <div className="account-settings-meta-card">
                      <span>{ta.accountAccessLevel}</span>
                      <strong>{isHeadAdmin ? ts.headAdmin : (userPosition || tc.admin)}</strong>
                    </div>
                    <div className="account-settings-meta-card">
                      <span>{ta.accountDepartment}</span>
                      <strong>{userPosition || ta.recruitmentHub}</strong>
                    </div>
                  </div>
                </aside>

                <div className="account-settings-details">
                  <div className="account-settings-columns">
                    <section className="account-settings-section account-settings-panel">
                      <h3>{ta.basicInfo}</h3>
                      <div className="account-settings-grid">
                        <div className="account-settings-row">
                          <div className="account-settings-row-icon account-settings-row-icon--name" aria-hidden="true" />
                          <div className="account-settings-row-content">
                            <span className="account-settings-label">{tc.name}</span>
                            <strong className="account-settings-value">{profileName || '—'}</strong>
                          </div>
                        </div>
                        <div className="account-settings-row account-settings-row--editable">
                          <div className="account-settings-row-icon account-settings-row-icon--email" aria-hidden="true" />
                          <div className="account-settings-row-content">
                            <span className="account-settings-label">{tc.email}</span>
                            {editingProfileField === 'email' ? (
                              <div className="account-settings-edit-group">
                                <input
                                  type="email"
                                  className="account-settings-input"
                                  value={profileEmailDraft}
                                  onChange={(e) => setProfileEmailDraft(e.target.value)}
                                />
                                <div className="account-settings-edit-actions">
                                  <button type="button" className="account-settings-save-btn" onClick={() => saveProfileField('email')}>{tc.save}</button>
                                  <button type="button" className="account-settings-cancel-btn" onClick={cancelProfileFieldEdit}>{tc.cancel}</button>
                                </div>
                              </div>
                            ) : (
                              <div className="account-settings-value-row">
                                <strong className="account-settings-value">{currentUser?.email || '—'}</strong>
                                <button
                                  type="button"
                                  className="account-settings-icon-btn account-settings-icon-btn--edit"
                                  aria-label={tc.edit}
                                  onClick={() => startProfileFieldEdit('email')}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="account-settings-section account-settings-panel account-settings-panel--account-info">
                      <h3>{ta.accountInfo}</h3>
                      <div className="account-settings-grid">
                        <div className="account-settings-row account-settings-row--editable">
                          <div className="account-settings-row-icon account-settings-row-icon--username" aria-hidden="true" />
                          <div className="account-settings-row-content">
                            <span className="account-settings-label">{ta.username}</span>
                            {editingProfileField === 'username' ? (
                              <div className="account-settings-edit-group">
                                <input
                                  type="text"
                                  className="account-settings-input"
                                  value={profileUsernameDraft}
                                  onChange={(e) => setProfileUsernameDraft(e.target.value)}
                                />
                                <div className="account-settings-edit-actions">
                                  <button type="button" className="account-settings-save-btn" onClick={() => saveProfileField('username')}>{tc.save}</button>
                                  <button type="button" className="account-settings-cancel-btn" onClick={cancelProfileFieldEdit}>{tc.cancel}</button>
                                </div>
                              </div>
                            ) : (
                              <div className="account-settings-value-row">
                                <strong className="account-settings-value">{profileUsername || '—'}</strong>
                                <button
                                  type="button"
                                  className="account-settings-icon-btn account-settings-icon-btn--edit"
                                  aria-label={tc.edit}
                                  onClick={() => startProfileFieldEdit('username')}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="account-settings-row account-settings-row--editable">
                          <div className="account-settings-row-icon account-settings-row-icon--password" aria-hidden="true" />
                          <div className="account-settings-row-content">
                            <span className="account-settings-label">{ta.password}</span>
                            <div className="account-settings-value-row">
                              <strong className="account-settings-value account-settings-value--masked">
                                {showProfilePassword ? '••••••••' : '********'}
                              </strong>
                              <button
                                type="button"
                                className={`account-settings-icon-btn account-settings-icon-btn--eye${showProfilePassword ? ' is-visible' : ''}`}
                                aria-label={showProfilePassword ? tc.hidePassword : tc.showPassword}
                                onClick={() => setShowProfilePassword((prev) => !prev)}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="account-settings-row">
                          <div className="account-settings-row-icon account-settings-row-icon--position" aria-hidden="true" />
                          <div className="account-settings-row-content">
                            <span className="account-settings-label">{ta.position}</span>
                            <strong className="account-settings-value">{userPosition || '—'}</strong>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                  {profileFieldNotice && (
                    <p className="account-settings-notice">{profileFieldNotice}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </main>

      {attachmentPreview && (
        <AttachmentInlineViewer
          label={attachmentPreview.items[attachmentPreview.index]?.label}
          url={attachmentPreview.items[attachmentPreview.index]?.url}
          items={attachmentPreview.items}
          activeIndex={attachmentPreview.index}
          onSelectIndex={handleSelectAttachmentIndex}
          onClose={() => setAttachmentPreview(null)}
          loading={attachmentPreview.loading}
          loadError={attachmentPreview.loadError}
          missingFilename={attachmentPreview.missingFilename}
        />
      )}

      <ProfileAvatarCropper
        open={Boolean(avatarCropSrc)}
        imageSrc={avatarCropSrc}
        saving={avatarUploading}
        title={ta.adjustProfilePhoto}
        hint={ta.adjustProfilePhotoHint}
        zoomLabel={ta.profilePhotoZoom}
        cancelLabel={tc.cancel}
        saveLabel={ta.saveProfilePhoto}
        savingLabel={ta.savingProfilePhoto}
        onCancel={handleCancelAvatarCrop}
        onConfirm={handleConfirmAvatarCrop}
      />
    </div>
    
  );
};

export default AdminDashboard;